/**
 * API Base URL Configuration
 *
 * Both student and portal apps run in the same container.
 * They use current domain + nginx proxy to backend.
 */

import { getSystemContext as getAppSystemContext } from './systemContext';

// Helper to clear auth store state (used in 401 handler)
// Uses localStorage key that Zustand persist middleware uses
const clearAuthStoreState = () => {
  // Clear Zustand persist storage key
  localStorage.removeItem('auth-storage');

  // Dispatch storage event to notify store (if store is subscribed)
  // This helps clear store state without circular dependency
  try {
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'auth-storage',
        newValue: null,
        oldValue: localStorage.getItem('auth-storage'),
      })
    );
  } catch (e) {
    // Ignore errors
  }
};

const getApiBaseUrl = () => {
  // Development environment
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api/v1';
  }

  // Production: Use current domain (nginx will proxy /api/ to backend)
  return `${window.location.origin}/api/v1`;
};

const API_BASE_URL = getApiBaseUrl();

export interface RegisterData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  password: string;
  systemContext?: 'VSAT' | 'THPT'; // Optional: defaults to VSAT on backend
}

export interface LoginData {
  identifier: string;
  password: string;
  systemContext?: 'VSAT' | 'THPT'; // Optional: defaults to VSAT on backend
}

export interface TeacherLoginData {
  email: string;
  password: string;
}

export interface EmployeeLoginData {
  email: string;
  password: string;
}

export interface VerifyOtpData {
  identifier: string;
  otp: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface SendPhoneOtpData {
  phone: string;
}

export interface ResetPasswordData {
  email: string;
  otp: string;
  newPassword: string;
}

export interface ResetPasswordWithTokenData {
  token: string;
  newPassword: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

// ===== DASHBOARD TYPES =====

// Teacher Dashboard Types
export interface TeacherDashboardStats {
  totalExams: number;
  totalQuestions: number;
  totalStudents: number;
  averageScore: number;
  recentActivity: {
    examsCreated: number;
    questionsCreated: number;
  };
}

export interface ExamListItem {
  id: string;
  title: string;
  subject: string;
  difficulty: string;
  duration: number;
  totalQuestions: number;
  status: string; // ExamStatus enum: DRAFT, VALIDATED, PUBLISHED, ACTIVE, COMPLETED, ARCHIVED
  isPremium: boolean;
  standard?: 'VSAT' | 'THPT' | null;
  questionsCount: number;
  sessionsCount: number;
  resultsCount: number;
  createdAt: string;
  createdById?: string; // ID of the user who created the exam
  createdBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  }; // Creator user object (if included in response)
  organizationId?: string | null; // Organization ID if exam belongs to an organization
  Organization?: {
    id: string;
    name: string;
    logo?: string; // URL to organization logo
  }; // Organization object (if included in response)
  type?: 'STATIC' | 'DYNAMIC'; // Exam type
  isPublished?: boolean; // Whether exam is published
  price?: number; // Exam price
}

export interface ExamListResponse {
  data: ExamListItem[];
  total: number;
  limit: number;
  offset: number;
  /** Whole-filtered-set aggregates (not just the current page) for quick-stat cards */
  stats?: {
    published: number;
    draft: number;
    archived: number;
    totalSessions: number;
  };
}

export interface QuestionListItem {
  id: string;
  stem: string; // The actual question text
  content: any; // JSON data for options/statements
  type: string;
  subject: string;
  difficulty: string;
  tags: string[];
  createdAt: string;
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  options?: QuestionOption[];
  _count?: {
    examQuestions: number;
  };
  clusterId?: string | null;
  cluster?: { id: string; name?: string } | null;
  collections?: Array<{
    collection?: { id: string; name: string; isQuestionBank?: boolean };
  }>;
  Organization?: { id?: string; name: string } | null;
  organizationId?: string | null;
}

export interface QuestionOption {
  id: string;
  content: string;
  isCorrect: boolean;
}

export interface QuestionListResponse {
  data: QuestionListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyticsFilters {
  date_from: string;
  date_to: string;
  exam_id?: string;
  teacher_id?: string;
  subject_id?: string;
  system?: 'ALL' | 'VSAT' | 'THPT' | 'BOTH';
  time_preset?: string;
}

/**
 * Dashboard analytics responses are dynamically-shaped backend aggregations
 * with no generated FE contract. Consumers narrow inner items at point of use
 * (e.g. `data.attemptsTrend.map((i: { date: string; value: number }) => ...)`).
 */
export interface DashboardAnalyticsResult {
  [key: string]: any;
}

export interface GAFilters {
  property?: 'VSAT' | 'THPT';
  startDate?: string;
  endDate?: string;
  limit?: number;
}

// ===== TEMPLATE TYPES =====

export interface ClusterConfigData {
  title: string;
  description?: string;
  questionCount: number;
  score?: number; // Score allocated to this cluster (points)
  selectionMode: 'INDIVIDUAL' | 'CLUSTER';
  // For INDIVIDUAL mode
  individual?: {
    questionType: string; // TF_MATRIX, MCQ, MATCHING, etc.
  };
  // For CLUSTER mode
  cluster?: {
    requirements: Array<{
      type: string;
      count: number;
    }>;
  };
  instructions?: string; // Instructions for this cluster
  scoringDetails?: any; // Scoring details (optional, can be auto-generated)
  questionCollectionId?: string;
  selectedTag?: string;
  filterRules?: {
    quantity?: number;
    tags?: string[];
    difficulty?: string;
    maxUsageCount?: number;
  };
  staticQuestionIds?: string[];
  clusterOrder?: number;
}

export interface TemplateConfigData {
  clusters: ClusterConfigData[];
  totalQuestions: number;
  totalScore: number;
  duration: number;
  scoringConfig?: any; // ExamScoringConfigDto
}

export interface CreateVsatTemplateData {
  subject: string;
  name: string;
  description?: string;
  config: TemplateConfigData;
}

export interface UpdateVsatTemplateData {
  subject?: string;
  name?: string;
  description?: string;
  config?: TemplateConfigData;
  isActive?: boolean;
}

export interface CreateThptTemplateData {
  subject: string;
  name: string;
  description?: string;
  config: TemplateConfigData;
}

export interface UpdateThptTemplateData {
  subject?: string;
  name?: string;
  description?: string;
  config?: TemplateConfigData;
  isActive?: boolean;
}

export interface CreateExamFromTemplateData {
  title: string;
  description?: string;
  organizationId?: string;
  difficulty?: string;
  isPremium?: boolean;
  price?: number;
}

export interface TemplateUsageStats {
  totalUsage: number;
  lastUsedAt?: string;
  usageByMonth: Array<{ month: string; count: number }>;
}

export interface TemplateVersion {
  id: string;
  templateId: string;
  templateType: 'VSAT' | 'THPT';
  version: number;
  config: any;
  name?: string;
  description?: string;
  createdBy?: string;
  createdAt: string;
  notes?: string;
}

export interface CreateExamData {
  title: string;
  description?: string;
  subject: string;
  type?: 'STATIC' | 'DYNAMIC'; // NEW: Exam type
  difficulty: string;
  duration: number;
  totalQuestions: number;
  passingScore: number;
  isPremium?: boolean;
  price?: number;
  questionIds?: string[];
  tags?: string[];
}

export interface UpdateExamData {
  title?: string;
  description?: string;
  subject?: string;
  type?: 'STATIC' | 'DYNAMIC'; // NEW: Exam type
  difficulty?: string;
  duration?: number;
  totalQuestions?: number;
  passingScore?: number;
  isPremium?: boolean;
  price?: number;
  status?: string; // ExamStatus enum
  organizationId?: string | null; // For transferring exam ownership
}

export interface CreateQuestionData {
  content: string;
  type: string;
  subject: string;
  difficulty: string;
  explanation?: string;
  imageUrl?: string;
  tags?: string[];
  options?: Array<{
    content: string;
    isCorrect: boolean;
  }>;
}

export interface UpdateQuestionData {
  content?: string;
  type?: string;
  subject?: string;
  difficulty?: string;
  explanation?: string;
  imageUrl?: string;
  tags?: string[];
  isActive?: boolean;
}

// ===== EXAM CLUSTER CONFIG TYPES =====

export interface ExamClusterConfig {
  id?: string;
  clusterOrder: number;
  title: string;
  description?: string;
  questionCount: number;
  selectionMode: 'INDIVIDUAL' | 'CLUSTER';
  questionCollectionId?: string; // For INDIVIDUAL/CLUSTER - link to Question Bank
  selectedTag?: string; // For INDIVIDUAL/CLUSTER - tag filter
  filterRules?: {
    quantity?: number;
    tags?: string[];
    difficulty?: string;
    maxUsageCount?: number;
  };
  staticQuestionIds?: string[]; // For INDIVIDUAL - pre-selected questions
}

export interface CreateExamWithClustersData extends CreateExamData {
  clusterConfigs?: ExamClusterConfig[];
}

// ===== QUESTION BANK TYPES =====

export interface QuestionBank {
  id: string;
  name: string;
  description?: string;
  subject: string;
  questionType: string; // MCQ, TRUE_FALSE, MATCHING, SHORT_ANSWER
  totalQuestions: number;
  status: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED';
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface QuestionBankDetail extends QuestionBank {
  statistics: {
    avgPValue: number; // Difficulty index (0-1)
    avgDValue: number; // Discrimination index (0-1)
    usageCount: number;
    typeDistribution: {
      [type: string]: number;
    };
    difficultyDistribution: {
      easy: number;
      medium: number;
      hard: number;
    };
    tagDistribution: {
      [tag: string]: number;
    };
  };
}

export interface QuestionBankListResponse {
  data: QuestionBank[];
  total: number;
  limit: number;
  offset: number;
}

export interface QuestionBankQuestion {
  id: string;
  content: string;
  type: string;
  difficulty: string;
  tags: string[];
  pValue: number; // Difficulty index
  dValue: number; // Discrimination index
  usageCount: number;
  lastUsed: string | null;
  createdAt: string;
  isActive: boolean;
}

export interface QuestionBankQuestionListResponse {
  data: QuestionBankQuestion[];
  total: number;
  limit: number;
  offset: number;
  // Một số endpoint trả shape khác (questions/pagination) — ClusterConfigStep probe cả hai
  questions?: QuestionBankQuestion[];
  pagination?: { total?: number; page?: number; limit?: number };
}

export interface PreviewSelectionRequest {
  questionBankId: string;
  quantity: number;
  selectedTag?: string | null | 'dynamic';
  filters?: {
    pValueRange?: [number, number];
    dValueMin?: number;
    maxUsageCount?: number;
    excludeTopics?: string[];
    mustHaveTags?: string[];
  };
}

export interface PreviewSelectionResponse {
  samples: Array<{
    questions: QuestionBankQuestion[];
    statistics: {
      avgPValue: number;
      avgDValue: number;
      difficultyDistribution: {
        easy: number;
        medium: number;
        hard: number;
      };
    };
  }>;
  warnings: string[];
}

export interface QuestionBankHealth {
  id: string;
  name: string;
  overallScore: number; // 0-100
  issues: Array<{
    type: 'insufficient_questions' | 'low_quality' | 'overused' | 'unused';
    severity: 'critical' | 'warning' | 'info';
    message: string;
    affectedTags?: string[];
    count?: number;
  }>;
  recommendations: string[];
  metrics: {
    totalQuestions: number;
    avgDValue: number;
    avgPValue: number;
    lowQualityCount: number; // D-value < 0.2
    overusedCount: number; // Usage > 50 in 7 days
    unusedCount: number; // Never used
    tagCoverage: {
      [tag: string]: {
        count: number;
        isHealthy: boolean;
      };
    };
  };
}

export interface StudentProgress {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  examsCompleted: number;
  lastActivity: string;
}

export interface StudentDetailedProgress {
  studentId: string;
  totalExamsTaken: number;
  averageScore: number;
  results: Array<{
    examId: string;
    examTitle: string;
    subject: string;
    score: number;
    percentage: number;
    passed: boolean;
    completedAt: string | null;
  }>;
}

export interface ExamResult {
  id: string;
  examId: string;
  score: number;
  percentage: number;
  passed: boolean;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  exam: {
    id: string;
    title: string;
    subject: string;
  };
}

// Admin Dashboard Types
export interface AdminDashboardStats {
  users: {
    total: number;
    byRole: Record<string, number>;
    recentSignups: number;
  };
  exams: {
    total: number;
  };
  questions: {
    total: number;
  };
  payments: {
    total: number;
    revenue: number;
    recentCount: number;
  };
}

export interface SystemStats {
  activeUsers: number;
  activeSessions: number;
  completedExams: number;
  totalRevenue: number;
}

export interface UserListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  permissions: string[];
  systemContext?: 'VSAT' | 'THPT'; // User's primary system
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  teacherProfile?: {
    schoolId: string;
    schoolName: string;
    department?: string;
    specialization?: string;
  };
  employeeProfile?: {
    employeeId: string;
    department?: string;
    position?: string;
  };
  _count: {
    createdExams: number;
    createdQuestions: number;
    examSessions: number;
  };
}

export interface UserListResponse {
  data: UserListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  permissions?: string[];
  systemContext?: 'VSAT' | 'THPT';
  isVerified?: boolean;
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  phone?: string;
  permissions?: string[];
  systemContext?: 'VSAT' | 'THPT';
  isVerified?: boolean;
  isActive?: boolean;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  userId: string;
  userEmail: string;
  userName: string;
  examId: string | null;
  examTitle: string | null;
  amount: number;
  originalAmount?: number;
  discountAmount?: number;
  discountCode?: string | null;
  currency: string;
  status: string;
  paymentMethod: string;
  transactionId?: string | null;
  misaTransactionId?: string | null;
  description?: string | null;
  qrCode?: string;
  transactionCode?: string;
  paidAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  refundedAt?: string | null;
  notes?: string | null;
  system?: string; // Derived system context (VSAT, THPT, etc.)
  misa?: {
    invoiceId: string;
    invoiceUrl: string;
    published: boolean;
    createdAt: string;
    lastSyncAt?: string;
    remoteStatus?: any;
  };
}

export interface PaymentListResponse {
  data: PaymentTransaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface PaymentStatistics {
  totalRevenue: number;
  mockRevenue: number; // Revenue from mock/test transactions
  pendingAmount: number;
  pendingCount: number;
  successRate: number;
  todayTransactions: number;
  monthOverMonthChange: { change: number; isPositive: boolean } | null;
  totalTransactions: number;
  completedTransactions: number;
}

export interface VerifyPaymentData {
  status: string;
  transactionCode?: string;
  notes?: string;
}

export interface ExamQueryParams {
  subject?: string;
  difficulty?: string;
  isPremium?: boolean;
  isPublished?: boolean;
  status?: string; // ExamStatus enum: DRAFT, VALIDATED, PUBLISHED, ACTIVE, COMPLETED, ARCHIVED
  standard?: 'VSAT' | 'THPT' | 'Custom' | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}

export interface QuestionQueryParams {
  search?: string; // search in question stem/content
  subject?: string;
  difficulty?: string;
  type?: string;
  tag?: string; // deprecated, use tags array
  tags?: string[]; // new: array of tags
  clusterId?: string; // filter by specific cluster
  hasCluster?: boolean; // filter by has/doesn't have cluster
  collectionId?: string; // filter by question bank/collection
  hasNoCollection?: boolean; // filter questions with no collection
  hasNoTags?: boolean; // filter questions with no tags
  isActive?: boolean;
  organizationId?: string; // filter by organization ID. Use "null" for personal questions
  limit?: number;
  offset?: number;
}

export interface UserQueryParams {
  role?: string;
  excludeRole?: string; // Exclude users with this role from results
  search?: string;
  isVerified?: boolean;
  isActive?: boolean;
  limit?: number;
  offset?: number;
  systemContext?: 'VSAT' | 'THPT' | 'BOTH'; // Filter users by system
}

export interface PaymentQueryParams {
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  date_from?: string;
  date_to?: string;
  userId?: string;
  examId?: string;
  teacherId?: string;
  systemContext?: 'ALL' | 'VSAT' | 'THPT' | 'BOTH';
  limit?: number;
  offset?: number;
  hasMisaInvoice?: boolean;
}

export type BidvReconcileCategory =
  | 'READY_TO_COMPLETE'
  | 'NEEDS_REVIEW'
  | 'ORPHAN_CREDIT'
  | 'AMOUNT_MISMATCH'
  | 'ALREADY_RECONCILED';
export type BidvReconcileAction = 'COMPLETE' | 'RECONCILE_ONLY' | 'MARK_RECONCILED' | 'NONE';

export interface BidvPreviewRow {
  bidvRef: string;
  transDateRaw: string;
  transDate?: string;
  creditAmount: number;
  content: string;
  category: BidvReconcileCategory;
  defaultAction: BidvReconcileAction;
  matchedOrderId?: string;
  matchedTxId?: string;
  dbStatus?: string;
  dbAmount?: number;
  userEmail?: string;
  reviewReasons?: string[];
}
export interface BidvPhantomRow {
  orderId: string;
  txId: string;
  amount: number;
  paidAt: string | null;
  userEmail?: string;
}
export interface BidvReconciliationPreview {
  uploadId: string;
  bankCode: string;
  period: { from: string | null; to: string | null };
  rowCount: number;
  creditRowCount: number;
  summary: Record<BidvReconcileCategory, number>;
  rows: BidvPreviewRow[];
  phantom: BidvPhantomRow[];
}
export interface BidvApplyResult {
  applied: number;
  completed: number;
  reconcileOnly: number;
  markedReconciled: number;
  skipped: number;
  errors: Array<{ bidvRef: string; reason: string }>;
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    permissions?: string[];
    systemContext?: 'VSAT' | 'THPT';
    schoolId?: string;
    schoolName?: string;
  };
}

export interface RegisterResponse {
  message: string;
  userId: string;
  isVerified: boolean;
}

// ===== SYSTEM CONFIGURATION TYPES =====

export interface SystemConfig {
  id?: string;
  key: string;
  value: any;
  description?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface CATReportPriceConfig {
  catReportPrice: number; // Price for CAT full report in VND
  currency: string;
  isDiscountEnabled?: boolean;
  discountPercentage?: number;
  discountEndDate?: string;
}

// Default config values (used when API is not available)
export const DEFAULT_CAT_REPORT_PRICE = 25000; // 25,000 VND

class ApiService {
  // System context for portal queries (optional, overrides JWT context)
  private portalSystemContext: 'VSAT' | 'THPT' | 'BOTH' | null = null;

  /**
   * Set system context for portal queries
   * This allows portal admins to query different systems
   */
  setSystemContext(systemContext: 'VSAT' | 'THPT' | 'BOTH' | null) {
    this.portalSystemContext = systemContext;
  }

  /**
   * Get system context for queries
   * Priority: 1. Portal system context (if set), 2. App's default system context
   */
  private getSystemContextForQuery(): string | null {
    // If portal has explicitly set a system context, use it
    if (this.portalSystemContext) {
      return this.portalSystemContext;
    }

    // Otherwise, use the app's default system context
    // This is set via VITE_SYSTEM_CONTEXT in each app's .env file
    // THPT app: VITE_SYSTEM_CONTEXT=THPT
    // VSAT app: VITE_SYSTEM_CONTEXT=VSAT
    return getAppSystemContext();
  }

  /**
   * Add system context to query parameters
   * This ensures all API requests include the correct systemContext
   */
  private addSystemContextToQuery(query: URLSearchParams): void {
    const systemContext = this.getSystemContextForQuery();
    if (systemContext) {
      query.append('systemContext', systemContext);
    }
  }

  /**
   * Add system context to URL
   * Handles both query string and URL with existing query params.
   * Does not overwrite systemContext if the URL already has it (e.g. from getPayments filter).
   */
  private addSystemContextToUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      if (urlObj.searchParams.has('systemContext')) {
        return url; // Preserve caller-provided systemContext (e.g. VSAT/THPT filter)
      }
    } catch {
      // Fall through to append below
    }
    const systemContext = this.getSystemContextForQuery();
    if (!systemContext) return url;

    try {
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set('systemContext', systemContext);
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, append as query param
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}systemContext=${systemContext}`;
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    let url = `${API_BASE_URL}${endpoint}`;

    // Only add systemContext query param for GET requests
    // For mutations (PUT/POST/etc), we rely on the X-System-Context header to avoid 404s
    const method = (options.method || 'GET').toUpperCase();
    const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

    if (!isMutation) {
      if (endpoint.includes('?') || endpoint.includes('&')) {
        url = this.addSystemContextToUrl(url);
      } else {
        const separator = endpoint.includes('?') ? '&' : '?';
        const systemContext = this.getSystemContextForQuery();
        if (systemContext) {
          url = `${url}${separator}systemContext=${systemContext}`;
        }
      }
    }

    const token = localStorage.getItem('accessToken');

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'X-VSAT-Bypass': 'website-login', // Bypass nginx basic auth
        // Add systemContext as header as well (for non-GET requests or when query params aren't suitable)
        'X-System-Context': this.getSystemContextForQuery() || '',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // If body is FormData, remove Content-Type header to let browser set it with boundary
    if (options.body instanceof FormData) {
      delete (config.headers as any)['Content-Type'];
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear auth storage (localStorage)
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        // Clear Zustand persist storage and trigger store update
        clearAuthStoreState();

        // List of public routes that don't require authentication
        // These routes should NOT redirect to login on 401
        const publicRoutes = [
          '/',
          '/kho-de-thi',
          '/exams',
          '/contact',
          '/help',
          '/early-access',
          '/privacy-policy',
          '/terms',
          '/data-deletion',
          '/chinh-sach-',
          '/gioi-thieu',
          '/samples/',
          '/test/',
          '/dinh-huong-nghe-nghiep',
          '/quy-doi-diem',
          '/conversion',
          '/doi-diem',
        ];

        const currentPath = window.location.pathname;
        const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
        const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/auth');

        // Only redirect to login if we're NOT on a public route and NOT already on the login page
        // This prevents redirect when token expires on public routes (e.g., /kho-de-thi)
        if (!isPublicRoute && !isOnLoginPage) {
          // Redirect to login page only if not on public route and not already there
          window.location.href = '/login';
        }

        const error: any = new Error('Session expired. Please login again.');
        error.response = {
          status: 401,
          data: errorData,
        };
        throw error;
      }

      const error: any = new Error(errorData.message || `HTTP ${response.status}`);
      // Preserve response data for error handling
      error.response = {
        status: response.status,
        data: errorData,
      };
      error.message = errorData.message || `HTTP ${response.status}`;
      throw error;
    }

    // Handle empty response (204 No Content or empty body)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // If response is empty or not JSON, return a success object
      return { success: true } as T;
    }

    // Try to parse JSON, but handle empty body gracefully
    const text = await response.text();
    if (!text || text.trim() === '') {
      return { success: true } as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch (e) {
      // If JSON parsing fails, return success object
      console.warn('Failed to parse JSON response, returning success:', e);
      return { success: true } as T;
    }
  }

  private buildAnalyticsQuery(filters?: AnalyticsFilters) {
    const params = new URLSearchParams();
    if (!filters) return '';
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.exam_id) params.append('exam_id', filters.exam_id);
    if (filters.teacher_id) params.append('teacher_id', filters.teacher_id);
    if (filters.subject_id) params.append('subject_id', filters.subject_id);
    if (filters.system) params.append('system', filters.system);
    return params.toString();
  }

  async sendFeedback(data: any): Promise<any> {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFeedbackList(params?: any): Promise<{ data: any[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          searchParams.append(key, params[key]);
        }
      });
    }
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return this.request<{ data: any[]; total: number }>(`/feedback${query}`, {
      method: 'GET',
    });
  }

  async updateFeedbackStatus(id: string, status: string): Promise<any> {
    return this.request(`/feedback/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async updateFeedbackNote(id: string, adminNote: string): Promise<any> {
    return this.request(`/feedback/${id}/note`, {
      method: 'PATCH',
      body: JSON.stringify({ adminNote }),
    });
  }

  async getFeedbackStats(): Promise<any> {
    return this.request('/feedback/stats', {
      method: 'GET',
    });
  }

  async uploadFile(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const isVideo = file.type.startsWith('video/');
    const endpoint = isVideo ? '/uploads/video' : '/uploads/image';

    return this.request(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async downloadFile(
    endpoint: string,
    options: { method?: string; body?: any } = {}
  ): Promise<Blob> {
    const url = `${API_BASE_URL}${endpoint}`;
    // Add system context if needed
    let finalUrl = url;

    // Only add system context to URL for GET requests
    if (!options.method || options.method === 'GET') {
      if (endpoint.includes('?') || endpoint.includes('&')) {
        finalUrl = this.addSystemContextToUrl(url);
      } else {
        const separator = endpoint.includes('?') ? '&' : '?';
        const systemContext = this.getSystemContextForQuery();
        if (systemContext) {
          finalUrl = `${url}${separator}systemContext=${systemContext}`;
        }
      }
    }

    const token = localStorage.getItem('accessToken');
    const headers: HeadersInit = {
      'X-VSAT-Bypass': 'website-login',
      'X-System-Context': this.getSystemContextForQuery() || '',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(options.body && { 'Content-Type': 'application/json' }),
    };

    const response = await fetch(finalUrl, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Download failed' }));
      const error: any = new Error(errorData.message || `HTTP ${response.status}`);
      error.response = {
        status: response.status,
        data: errorData,
      };
      throw error;
    }

    return await response.blob();
  }

  async validateImport(formData: FormData): Promise<any> {
    return this.request('/question-collections/validate-import', {
      method: 'POST',
      body: formData,
    });
  }

  async importCollection(formData: FormData): Promise<any> {
    return this.request('/question-collections/import', {
      method: 'POST',
      body: formData,
    });
  }

  // Auth endpoints
  async register(data: RegisterData): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: LoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginTeacher(data: TeacherLoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/dashboard/teacher/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginEmployee(data: EmployeeLoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/dashboard/employee/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginDashboard(data: EmployeeLoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/dashboard/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginAdmin(data: EmployeeLoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/dashboard/admin/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyOtp(data: VerifyOtpData): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyEmailToken(token: string): Promise<{ message: string; accessToken?: string }> {
    return this.request<{ message: string; accessToken?: string }>(`/auth/verify-email/${token}`, {
      method: 'GET',
    });
  }

  async resendOtp(identifier: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    });
  }

  async forgotPassword(data: ForgotPasswordData): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendPhoneOtp(data: SendPhoneOtpData): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/send-phone-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPassword(data: ResetPasswordData): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPasswordWithToken(data: ResetPasswordWithTokenData): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/auth/reset-password/${data.token}`, {
      method: 'POST',
      body: JSON.stringify({ newPassword: data.newPassword }),
    });
  }

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Health check
  async healthCheck(): Promise<any> {
    return this.request('/health');
  }

  // User profile
  async getProfile(): Promise<any> {
    return this.request('/auth/profile');
  }

  async updateProfile(data: any): Promise<any> {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadAvatar(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('avatar', file);

    const token = localStorage.getItem('accessToken');

    return fetch(`${API_BASE_URL}/auth/avatar`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-VSAT-Bypass': 'website-login', // Bypass nginx basic auth
      },
      body: formData,
    }).then(async response => {
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || response.statusText);
      }
      return response.json();
    });
  }

  async getLoginHistory(): Promise<any> {
    return this.request('/auth/login-history');
  }

  // ========== TEACHER DASHBOARD ENDPOINTS ==========

  // Dashboard stats
  async getTeacherDashboardStats(): Promise<TeacherDashboardStats> {
    return this.request<TeacherDashboardStats>('/teacher/dashboard/stats');
  }

  // Exam management
  async getTeacherExams(
    params?: ExamQueryParams & {
      status?: string;
      organizationId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<ExamListResponse> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.subject) query.append('subject', params.subject);
    if (params?.difficulty) query.append('difficulty', params.difficulty);
    if (params?.standard) query.append('standard', params.standard);
    if (params?.status) query.append('status', params.status);
    if (params?.organizationId) query.append('organizationId', params.organizationId);
    if (params?.isPremium !== undefined) query.append('isPremium', String(params.isPremium));
    if (params?.isPublished !== undefined) query.append('isPublished', String(params.isPublished));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.sortBy) query.append('sortBy', params.sortBy);
    if (params?.sortOrder) query.append('sortOrder', params.sortOrder);

    const queryString = query.toString();
    return this.request<ExamListResponse>(`/teacher/exams${queryString ? `?${queryString}` : ''}`);
  }

  async getTeacherExamById(examId: string): Promise<any> {
    return this.request(`/teacher/exams/${examId}`);
  }

  async createExam(data: CreateExamData): Promise<any> {
    return this.request('/teacher/exams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createExamWithClusters(data: CreateExamWithClustersData): Promise<any> {
    return this.request('/teacher/exams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExam(examId: string, data: UpdateExamData): Promise<any> {
    return this.request(`/teacher/exams/${examId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addClusterConfig(examId: string, config: ExamClusterConfig): Promise<any> {
    return this.request(`/teacher/exams/${examId}/clusters`, {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async getExamClusters(examId: string): Promise<ExamClusterConfig[]> {
    return this.request(`/teacher/exams/${examId}/clusters`);
  }

  async updateClusterConfig(
    examId: string,
    configId: string,
    config: Partial<ExamClusterConfig>
  ): Promise<any> {
    return this.request(`/teacher/exams/${examId}/clusters/${configId}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteClusterConfig(examId: string, configId: string): Promise<any> {
    return this.request(`/teacher/exams/${examId}/clusters/${configId}`, {
      method: 'DELETE',
    });
  }

  async deleteExam(
    examId: string,
    permanent: boolean = false
  ): Promise<{ message: string; permanent?: boolean }> {
    const url = `/teacher/exams/${examId}${permanent ? '?permanent=true' : ''}`;
    return this.request<{ message: string; permanent?: boolean }>(url, {
      method: 'DELETE',
    });
  }

  async publishExam(examId: string): Promise<any> {
    return this.request(`/teacher/exams/${examId}/publish`, {
      method: 'POST',
    });
  }

  // Exam Lifecycle Management
  async validateExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/validate`, {
      method: 'POST',
    });
  }

  async publishExamWithOptions(examId: string, dto: any): Promise<any> {
    return this.request(`/exams/${examId}/publish`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async unpublishExam(examId: string, reason: string): Promise<any> {
    return this.request(`/exams/${examId}/unpublish`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async archiveExam(examId: string, reason: string): Promise<any> {
    return this.request(`/exams/${examId}/archive`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async unarchiveExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/unarchive`, {
      method: 'POST',
    });
  }

  async getExamStatusHistory(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/status-history`);
  }

  // Exam Scheduling
  async scheduleExam(examId: string, dto: any): Promise<any> {
    return this.request(`/exams/${examId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async updateExamSchedule(examId: string, dto: any): Promise<any> {
    return this.request(`/exams/${examId}/schedule`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  }

  async cancelExamSchedule(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/schedule`, {
      method: 'DELETE',
    });
  }

  async getExamSchedule(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/schedule`);
  }

  async getLiveActivity(): Promise<any> {
    return this.request('/exams/live-activity');
  }

  async activateExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/activate`, {
      method: 'POST',
    });
  }

  async completeExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/complete`, {
      method: 'POST',
    });
  }

  // Exam Preview
  async generateExamPreview(
    examId: string,
    options?: { regenerate?: boolean; includeContent?: boolean }
  ): Promise<any> {
    return this.request(`/exams/${examId}/preview`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }

  async clearExamPreviewCache(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/preview`, {
      method: 'DELETE',
    });
  }

  /**
   * Dry run: Generate questions preview for dynamic exam without saving
   * This is used to preview what questions would be generated for a dynamic exam
   */
  async dryRunGenerateQuestions(examId: string): Promise<{
    success: boolean;
    questions?: any[];
    error?: { message: string; code?: string };
  }> {
    return this.request(`/exams/${examId}/dry-run`, {
      method: 'POST',
    });
  }

  // Exam Analytics
  async getExamAnalytics(examId: string): Promise<any> {
    return this.request(`/analytics/exams/${examId}`);
  }

  async getOrganizationAnalytics(orgId: string): Promise<any> {
    return this.request(`/analytics/organizations/${orgId}`);
  }

  async getDashboardAnalytics(startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard${queryString ? `?${queryString}` : ''}`
    );
  }

  async getAnalyticsOverview(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/overview${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsRevenue(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/revenue${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsUsers(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/users${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsExams(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/exams${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsTopExams(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/top-exams${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsTopStudents(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/top-students${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsStudentBehavior(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/student-behavior${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsTeacherStats(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/teacher-stats${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsQuestionBank(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/question-bank${query ? `?${query}` : ''}`
    );
  }

  async getAnalyticsTraffic(filters: AnalyticsFilters) {
    const query = this.buildAnalyticsQuery(filters);
    return this.request<DashboardAnalyticsResult>(
      `/analytics/dashboard/traffic${query ? `?${query}` : ''}`
    );
  }

  async getKpiTargets() {
    return this.request('/analytics/kpi/targets');
  }

  async upsertKpiTarget(data: { metric: string; targetValue: number; period: string }) {
    return this.request('/analytics/kpi/targets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Google Analytics 4
  private buildGAQuery(filters?: GAFilters) {
    const params = new URLSearchParams();
    if (!filters) return '';
    if (filters.property) params.append('property', filters.property);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.limit) params.append('limit', String(filters.limit));
    return params.toString();
  }

  async getGAStatus() {
    return this.request('/analytics/ga/status');
  }

  async getGADashboard(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/dashboard${query ? `?${query}` : ''}`);
  }

  async getGARealtime(property?: string) {
    const query = property ? `?property=${property}` : '';
    return this.request(`/analytics/ga/realtime${query}`);
  }

  async getGAOverview(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/overview${query ? `?${query}` : ''}`);
  }

  async getGATrafficSources(filters?: GAFilters & { detailed?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.property) params.append('property', filters.property);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.detailed) params.append('detailed', 'true');
    const query = params.toString();
    return this.request(`/analytics/ga/traffic-sources${query ? `?${query}` : ''}`);
  }

  async getGATopPages(filters?: GAFilters & { channel?: string }) {
    const params = new URLSearchParams();
    if (filters?.property) params.append('property', filters.property);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.channel) params.append('channel', filters.channel);
    if (filters?.limit) params.append('limit', String(filters.limit));
    const query = params.toString();
    return this.request(`/analytics/ga/top-pages${query ? `?${query}` : ''}`);
  }

  async getGAAllPages(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/all-pages${query ? `?${query}` : ''}`);
  }

  async getGADevices(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/devices${query ? `?${query}` : ''}`);
  }

  async getGADailyTrend(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/daily-trend${query ? `?${query}` : ''}`);
  }

  async getGAGeography(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/geography${query ? `?${query}` : ''}`);
  }

  async getGABrowsers(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/browsers${query ? `?${query}` : ''}`);
  }

  async getGAHourly(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/hourly${query ? `?${query}` : ''}`);
  }

  async getGAUserTypes(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/user-types${query ? `?${query}` : ''}`);
  }

  async getGAOperatingSystems(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/os${query ? `?${query}` : ''}`);
  }

  async getGAScreenResolutions(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/screen-resolutions${query ? `?${query}` : ''}`);
  }

  async getGAEvents(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/events${query ? `?${query}` : ''}`);
  }

  async getGALandingPages(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/landing-pages${query ? `?${query}` : ''}`);
  }

  async getGASearchTerms(filters?: GAFilters) {
    const query = this.buildGAQuery(filters);
    return this.request(`/analytics/ga/search-terms${query ? `?${query}` : ''}`);
  }

  // Exam Operations
  async cloneExam(examId: string, dto: any): Promise<any> {
    return this.request(`/exams/${examId}/clone`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async saveExamAsTemplate(examId: string, dto: any): Promise<any> {
    return this.request(`/exams/${examId}/save-template`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async loadExamFromTemplate(dto: any): Promise<any> {
    return this.request('/exams/from-template', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async bulkOperation(dto: any): Promise<any> {
    return this.request('/exams/bulk', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async exportExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/export`);
  }

  async importExam(jsonData: any): Promise<any> {
    return this.request('/exams/import', {
      method: 'POST',
      body: JSON.stringify(jsonData),
    });
  }

  // Admin exam management
  async getAllExams(
    params?: ExamQueryParams & {
      status?: string;
      organizationId?: string;
      systemContext?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<ExamListResponse> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.subject) query.append('subject', params.subject);
    if (params?.difficulty) query.append('difficulty', params.difficulty);
    if (params?.standard) query.append('standard', params.standard);
    if (params?.status) query.append('status', params.status);
    if (params?.organizationId) query.append('organizationId', params.organizationId);
    if (params?.isPremium !== undefined) query.append('isPremium', String(params.isPremium));
    if (params?.isPublished !== undefined) query.append('isPublished', String(params.isPublished));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.systemContext) query.append('systemContext', params.systemContext);
    if (params?.sortBy) query.append('sortBy', params.sortBy);
    if (params?.sortOrder) query.append('sortOrder', params.sortOrder);

    return this.request<ExamListResponse>(`/admin/exams?${query.toString()}`);
  }

  async getExamDetails(examId: string): Promise<any> {
    return this.request(`/admin/exams/${examId}`);
  }

  async addQuestionsToExam(examId: string, questionIds: string[]): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/teacher/exams/${examId}/questions`, {
      method: 'POST',
      body: JSON.stringify({ questionIds }),
    });
  }

  // Question management
  async getTeacherQuestions(params?: QuestionQueryParams): Promise<QuestionListResponse> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.subject) query.append('subject', params.subject);
    if (params?.difficulty) query.append('difficulty', params.difficulty);
    if (params?.type) query.append('type', params.type);
    // Backward compatibility: single tag
    if (params?.tag) query.append('tag', params.tag);
    // New: multiple tags (array)
    if (params?.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => query.append('tags', tag));
    }
    // New: cluster filter
    if (params?.clusterId) query.append('clusterId', params.clusterId);
    if (params?.hasCluster !== undefined) query.append('hasCluster', String(params.hasCluster));
    // New: collection filter
    if (params?.collectionId) query.append('collectionId', params.collectionId);
    if (params?.hasNoCollection !== undefined)
      query.append('hasNoCollection', String(params.hasNoCollection));
    if (params?.hasNoTags !== undefined) query.append('hasNoTags', String(params.hasNoTags));
    if (params?.isActive !== undefined) query.append('isActive', String(params.isActive));
    if (params?.organizationId !== undefined) {
      query.append('organizationId', params.organizationId);
    }
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));

    const queryString = query.toString();
    return this.request<QuestionListResponse>(
      `/teacher/questions${queryString ? `?${queryString}` : ''}`
    );
  }

  async getTeacherQuestionById(questionId: string): Promise<any> {
    return this.request(`/teacher/questions/${questionId}`);
  }

  async getTeacherQuestionStats(organizationId?: string | null): Promise<any> {
    const params = organizationId
      ? `?organizationId=${organizationId}`
      : organizationId === null
        ? '?organizationId=null'
        : '';
    return this.request(`/teacher/questions/stats${params}`);
  }

  // Admin question management
  async getAllQuestions(params: any = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle array values (tags)
        if (Array.isArray(value)) {
          value.forEach(item => queryParams.append(key, String(item)));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });

    const queryString = queryParams.toString();
    const url = `/admin/questions${queryString ? `?${queryString}` : ''}`;

    return this.request(url);
  }

  async createTeacherQuestion(data: CreateQuestionData): Promise<any> {
    const token = localStorage.getItem('accessToken');
    console.log('Creating question with token:', token ? 'Present' : 'Missing');
    console.log('Question data:', data);

    return this.request('/teacher/questions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTeacherQuestion(questionId: string, data: UpdateQuestionData): Promise<any> {
    return this.request(`/teacher/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTeacherQuestion(questionId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/teacher/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  async exportQuestions(questionIds?: string[]): Promise<any> {
    const query =
      questionIds && questionIds.length > 0 ? `?questionIds=${questionIds.join(',')}` : '';
    return this.request(`/teacher/questions/export${query}`);
  }

  async importQuestions(file: File, organizationId?: string | null): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    // Workspace đích cho câu hỏi import (backend xác thực membership);
    // không gửi thì backend fallback hành vi cũ
    if (organizationId) {
      formData.append('organizationId', organizationId);
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    const baseUrl =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:3001/api/v1'
        : `${window.location.origin}/api/v1`;

    const response = await fetch(`${baseUrl}/teacher/questions/import`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Import failed');
    }

    return response.json();
  }

  // Student tracking
  async getTeacherStudents(): Promise<StudentProgress[]> {
    return this.request<StudentProgress[]>('/teacher/students');
  }

  async getStudentProgress(studentId: string): Promise<StudentDetailedProgress> {
    return this.request<StudentDetailedProgress>(`/teacher/students/${studentId}/progress`);
  }

  // Exam results
  async getTeacherExamResults(examId?: string): Promise<ExamResult[]> {
    const query = examId ? `?examId=${examId}` : '';
    return this.request<ExamResult[]>(`/teacher/results${query}`);
  }

  // ========== ADMIN DASHBOARD ENDPOINTS ==========

  // Dashboard stats
  async getAdminDashboardStats(): Promise<AdminDashboardStats> {
    return this.request<AdminDashboardStats>('/admin/dashboard/stats');
  }

  async getSystemStats(): Promise<SystemStats> {
    return this.request<SystemStats>('/admin/system/stats');
  }

  // ===== SYSTEM CONFIGURATION =====

  /**
   * Get all system configurations
   */
  async getSystemConfigs(): Promise<SystemConfig[]> {
    try {
      return await this.request<SystemConfig[]>('/system-config');
    } catch (error) {
      console.warn('Failed to fetch system configs from API, using localStorage');
      // Fallback to localStorage
      const configs = localStorage.getItem('system_configs');
      return configs ? JSON.parse(configs) : [];
    }
  }

  /**
   * Get a specific configuration by key
   */
  async getConfigByKey(key: string): Promise<SystemConfig | null> {
    try {
      return await this.request<SystemConfig>(`/system-config/${key}`);
    } catch (error) {
      // If 404, return null (handled by caller fallback to default constant)
      // Do NOT fallback to localStorage
      return null;
    }
  }

  /**
   * Update a system configuration (admin only)
   */
  async updateConfig(key: string, value: any, description?: string): Promise<SystemConfig> {
    const result = await this.request<SystemConfig>(`/system-config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description }),
    });
    return result;
  }

  /**
   * Get CAT report price configuration
   * Returns the price for the full CAT report
   */
  async getCATReportPrice(): Promise<number> {
    const config = await this.getConfigByKey('catReportPrice');
    return config?.value ? Number(config.value) : DEFAULT_CAT_REPORT_PRICE;
  }

  /**
   * Update CAT report price (admin only)
   */
  async updateCATReportPrice(price: number): Promise<SystemConfig> {
    return this.updateConfig('catReportPrice', price, 'Price for CAT full report in VND');
  }

  // User management
  async getUsers(params?: UserQueryParams): Promise<UserListResponse> {
    const query = new URLSearchParams();
    if (params?.role) query.append('role', params.role);
    if (params?.excludeRole) query.append('excludeRole', params.excludeRole);
    if (params?.search) query.append('search', params.search);
    if (params?.isVerified !== undefined) query.append('isVerified', String(params.isVerified));
    if (params?.isActive !== undefined) query.append('isActive', String(params.isActive));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    // Add systemContext filter for portal multi-system support
    if (params?.systemContext) query.append('systemContext', params.systemContext);

    const queryString = query.toString();
    return this.request<UserListResponse>(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUserById(userId: string): Promise<UserListItem> {
    return this.request<UserListItem>(`/admin/users/${userId}`);
  }

  async createUser(data: CreateUserData): Promise<any> {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId: string, data: UpdateUserData): Promise<any> {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Payment management
  async getPayments(params?: PaymentQueryParams): Promise<PaymentListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);
    if (params?.date_from) query.append('date_from', params.date_from);
    if (params?.date_to) query.append('date_to', params.date_to);
    if (params?.userId) query.append('userId', params.userId);
    if (params?.examId) query.append('examId', params.examId);
    if (params?.teacherId) query.append('teacherId', params.teacherId);
    if (params?.systemContext) {
      query.append('systemContext', params.systemContext);
    }
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.hasMisaInvoice !== undefined) {
      query.append('hasMisaInvoice', String(params.hasMisaInvoice));
    }

    const queryString = query.toString();
    return this.request<PaymentListResponse>(
      `/admin/payments${queryString ? `?${queryString}` : ''}`
    );
  }

  // Get current user's payments (student-facing)
  async getMyPayments(params?: {
    status?: string;
    examId?: string;
    assessmentId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaymentListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.examId) query.append('examId', params.examId);
    if (params?.assessmentId) query.append('assessmentId', params.assessmentId);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));

    const queryString = query.toString();
    return this.request<PaymentListResponse>(`/payment/my${queryString ? `?${queryString}` : ''}`);
  }

  async verifyPayment(paymentId: string, data: VerifyPaymentData): Promise<any> {
    return this.request(`/admin/payments/${paymentId}/verify`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPaymentStatistics(): Promise<PaymentStatistics> {
    return this.request<PaymentStatistics>('/payment/statistics');
  }

  // ========== STUDENT PAYMENT ENDPOINTS ==========

  /**
   * Create a payment session
   */
  async createPayment(data: {
    amount: number;
    examId?: string;
    assessmentId?: string;
    description?: string;
    orderId?: string;
    orderPrefix?: 'CAT' | 'VSAT';
    isMock?: boolean;
    accessCode?: string;
    sourceApp?: 'VSAT' | 'THPT';
    paymentPurpose?: 'EXAM_PURCHASE' | 'TOP_UP';
    buyerCompanyName?: string;
    buyerTaxCode?: string;
    buyerCompanyAddress?: string;
    buyerFullName?: string;
  }): Promise<{
    transactionId: string;
    orderId: string;
    amount: number;
    originalAmount?: number;
    discountAmount?: number;
    status: string;
    qrCode?: string;
    qrData?: string;
    accountNo?: string;
    accountName?: string;
    bankName?: string;
    expiresAt?: string;
    paymentUrl?: string;
  }> {
    return this.request('/payment/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get payment status by transaction ID
   */
  async getPaymentStatus(transactionId: string): Promise<{
    status: string;
    transactionId: string;
    orderId?: string;
    amount?: number;
  }> {
    return this.request(`/payment/status/${transactionId}`);
  }

  /**
   * Check payment status for a specific exam (for students)
   * Returns whether the current user has completed payment for the exam
   */
  async checkExamPaymentStatus(examId: string): Promise<{
    hasPayment: boolean;
    payment: {
      id: string;
      status: string;
      amount: number;
      createdAt: string;
      exam: {
        id: string;
        title: string;
      };
    } | null;
  }> {
    return this.request(`/exams/payment-status?examId=${examId}`);
  }

  async getQuestionStats(): Promise<any> {
    return this.request('/questions/stats/summary');
  }

  // ========== UPLOAD ENDPOINTS ==========

  async uploadImage(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('accessToken');

    const response = await fetch(`${API_BASE_URL}/uploads/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-VSAT-Bypass': 'website-login',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async uploadAudio(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('accessToken');

    const response = await fetch(`${API_BASE_URL}/uploads/audio`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-VSAT-Bypass': 'website-login',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async deleteImage(filename: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/uploads/image/${filename}`, {
      method: 'DELETE',
    });
  }

  // ========== QUESTION BANK ENDPOINTS ==========

  // Questions (Enhanced)
  async getQuestions(filters?: any): Promise<{ questions: any[]; pagination: any }> {
    const query = new URLSearchParams();
    if (filters?.search) query.append('search', filters.search);
    if (filters?.subject) query.append('subject', filters.subject);
    if (filters?.difficulty) query.append('difficulty', filters.difficulty);
    if (filters?.type) query.append('type', filters.type);
    if (filters?.bloomLevel) query.append('bloomLevel', filters.bloomLevel);
    if (filters?.tag) query.append('tag', filters.tag);
    if (filters?.categoryId) query.append('categoryId', filters.categoryId);
    if (filters?.clusterId) query.append('clusterId', filters.clusterId);
    if (filters?.collectionId) query.append('collectionId', filters.collectionId);
    if (filters?.hasNoCollection !== undefined)
      query.append('hasNoCollection', String(filters.hasNoCollection));
    if (filters?.hasNoTags !== undefined) query.append('hasNoTags', String(filters.hasNoTags));
    if (filters?.createdById) query.append('createdById', filters.createdById);
    if (filters?.isPublic !== undefined) query.append('isPublic', String(filters.isPublic));
    if (filters?.isActive !== undefined) query.append('isActive', String(filters.isActive));
    if (filters?.minQuality) query.append('minQuality', String(filters.minQuality));
    if (filters?.minUsage) query.append('minUsage', String(filters.minUsage));
    if (filters?.page) query.append('page', String(filters.page));
    if (filters?.limit) query.append('limit', String(filters.limit));
    if (filters?.sortBy) query.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) query.append('sortOrder', filters.sortOrder);
    // Handle tags array
    if (filters?.tags && Array.isArray(filters.tags) && filters.tags.length > 0) {
      filters.tags.forEach((tag: string) => query.append('tags', tag));
    }

    // Add system context for portal queries
    this.addSystemContextToQuery(query);

    const queryString = query.toString();
    return this.request<{ questions: any[]; pagination: any }>(
      `/questions${queryString ? `?${queryString}` : ''}`
    );
  }

  async getQuestionById(id: string): Promise<any> {
    return this.request(`/questions/${id}`);
  }

  async createQuestion(data: any): Promise<any> {
    return this.request('/questions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuestion(id: string, data: any): Promise<any> {
    return this.request(`/questions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteQuestion(id: string): Promise<void> {
    return this.request(`/questions/${id}`, {
      method: 'DELETE',
    });
  }

  async getQuestionBankStats(): Promise<any> {
    return this.request('/questions/stats/summary');
  }

  // Question Clusters
  async getQuestionClusters(filters?: any): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.search) query.append('search', filters.search);
    if (filters?.type) query.append('type', filters.type);
    if (filters?.subject) query.append('subject', filters.subject);
    if (filters?.createdById) query.append('createdById', filters.createdById);

    const queryString = query.toString();
    return this.request<any[]>(`/question-clusters${queryString ? `?${queryString}` : ''}`);
  }

  async getQuestionClusterById(id: string): Promise<any> {
    return this.request(`/question-clusters/${id}`);
  }

  async createQuestionCluster(data: any): Promise<any> {
    return this.request('/question-clusters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuestionCluster(id: string, data: any): Promise<any> {
    return this.request(`/question-clusters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createExampleTfMatrixCluster(): Promise<any> {
    return this.request('/question-clusters/example/tf-matrix-cluster', {
      method: 'POST',
    });
  }

  async deleteQuestionCluster(id: string): Promise<void> {
    return this.request(`/question-clusters/${id}`, {
      method: 'DELETE',
    });
  }

  // Question Categories
  async getQuestionCategories(filters?: any): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.search) query.append('search', filters.search);
    if (filters?.subject) query.append('subject', filters.subject);
    if (filters?.parentId !== undefined) query.append('parentId', filters.parentId);
    if (filters?.level !== undefined) query.append('level', String(filters.level));

    const queryString = query.toString();
    return this.request<any[]>(`/question-categories${queryString ? `?${queryString}` : ''}`);
  }

  async getQuestionCategoryTree(subject?: string): Promise<any[]> {
    const query = subject ? `?subject=${subject}` : '';
    return this.request<any[]>(`/question-categories/tree${query}`);
  }

  async getQuestionCategoryById(id: string): Promise<any> {
    return this.request(`/question-categories/${id}`);
  }

  async getQuestionCategoryPath(id: string): Promise<any[]> {
    return this.request<any[]>(`/question-categories/${id}/path`);
  }

  async createQuestionCategory(data: any): Promise<any> {
    return this.request('/question-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuestionCategory(id: string, data: any): Promise<any> {
    return this.request(`/question-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteQuestionCategory(id: string): Promise<void> {
    return this.request(`/question-categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Question Collections
  async getQuestionCollections(filters?: any): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.search) query.append('search', filters.search);
    if (filters?.subject) query.append('subject', filters.subject);
    if (filters?.createdById) query.append('createdById', filters.createdById);
    if (filters?.isPublic !== undefined) query.append('isPublic', String(filters.isPublic));
    if (filters?.isQuestionBank !== undefined)
      query.append('isQuestionBank', String(filters.isQuestionBank));
    if (filters?.status) query.append('status', filters.status);

    // Handle organizationId filter - support null for personal collections
    if (filters?.organizationId !== undefined) {
      if (filters.organizationId === null) {
        // Send 'null' string to backend to filter personal collections
        query.append('organizationId', 'null');
      } else {
        query.append('organizationId', filters.organizationId);
      }
    }

    // Add system context for portal queries
    this.addSystemContextToQuery(query);

    const queryString = query.toString();
    return this.request<any[]>(`/question-collections${queryString ? `?${queryString}` : ''}`);
  }

  async getQuestionCollectionById(id: string): Promise<any> {
    return this.request(`/question-collections/${id}`);
  }

  async createQuestionCollection(data: any): Promise<any> {
    return this.request('/question-collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateQuestionCollection(id: string, data: any): Promise<any> {
    return this.request(`/question-collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteQuestionCollection(id: string): Promise<void> {
    return this.request(`/question-collections/${id}`, {
      method: 'DELETE',
    });
  }

  async addQuestionsToCollection(collectionId: string, questionIds: string[]): Promise<any> {
    return this.request(`/question-collections/${collectionId}/questions`, {
      method: 'POST',
      body: JSON.stringify({ questionIds }),
    });
  }

  async removeQuestionFromCollection(collectionId: string, questionId: string): Promise<void> {
    return this.request(`/question-collections/${collectionId}/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  async reorderQuestionInCollection(
    collectionId: string,
    questionId: string,
    newOrder: number
  ): Promise<any> {
    return this.request(`/question-collections/${collectionId}/questions/${questionId}/order`, {
      method: 'PUT',
      body: JSON.stringify({ newOrder }),
    });
  }

  async publishQuestionBank(collectionId: string): Promise<any> {
    return this.request(`/question-collections/${collectionId}/publish`, {
      method: 'PUT',
    });
  }

  /**
   * Get all collections/question banks that contain a specific question
   */
  async getCollectionsByQuestionId(questionId: string): Promise<any[]> {
    try {
      const response = await this.request<any[]>(`/question-collections/by-question/${questionId}`);
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error fetching collections for question:', error);
      return [];
    }
  }

  // ========== STUDENT EXAM ENDPOINTS ==========

  /**
   * Get all exams with filtering options
   */
  async getExams(params?: ExamQueryParams): Promise<any> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }

    // Add system context for portal queries
    this.addSystemContextToQuery(searchParams);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/exams?${queryString}` : '/exams';

    return this.request(endpoint);
  }

  /**
   * Get exams ready for students to take
   */
  async getReadyExams(params?: ExamQueryParams): Promise<any> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }

    // Add system context for portal queries
    this.addSystemContextToQuery(searchParams);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/exams/ready?${queryString}` : '/exams/ready';

    return this.request(endpoint);
  }

  /**
   * Get exam by ID
   */
  async getExamById(id: string): Promise<any> {
    return this.request(`/exams/${id}`);
  }

  /**
   * Get exam details for student (only if ready)
   */
  async getExamForStudent(id: string): Promise<any> {
    return this.request(`/exams/${id}/student`);
  }

  /**
   * Get exam statistics
   */
  async getExamStats(): Promise<any> {
    return this.request('/exams/stats');
  }

  /**
   * Get exam questions
   */
  async getExamQuestions(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/questions`);
  }

  /**
   * Pre-check exam generation
   * Validates if exam can be generated and pre-creates a session
   */
  async preCheckExam(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/pre-check`, {
      method: 'POST',
    });
  }

  /**
   * Start exam session
   */
  async startExamSession(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/start`, {
      method: 'POST',
    });
  }

  /**
   * Get exam session status and remaining time
   * Returns session info if exists, or null if no active session
   */
  async getSessionStatus(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/session/status`);
  }

  /**
   * Get current active session for user (across all exams)
   */
  async getActiveSessionForUser(): Promise<any> {
    return this.request(`/exams/session/active`);
  }

  /**
   * Get all incomplete sessions for user
   */
  async getIncompleteSessions(): Promise<any[]> {
    return this.request<any[]>('/exams/sessions/incomplete');
  }

  /**
   * Lazy load questions for exam session
   * Returns only questions around the current question index
   * Used for incremental loading instead of loading entire exam at once
   *
   * @param examId - The exam ID
   * @param current - Current question number (1-indexed)
   * @param range - Questions to load before/after current (default: 1, max: 5)
   */
  async getSessionQuestions(
    examId: string,
    current: number,
    range: number = 1
  ): Promise<{
    questions: any[];
    totalQuestions: number;
    currentRange: { start: number; end: number };
    clusters: Record<string, any>;
    sessionId: string;
    timeRemaining: number | null;
    questionMap: Array<{ id: string; order: number; type: string; clusterId?: string }>;
    examInfo?: any;
  }> {
    const query = new URLSearchParams();
    query.append('current', String(current));
    query.append('range', String(Math.min(range, 5)));
    return this.request(`/exams/${examId}/session/questions?${query.toString()}`);
  }

  /**
   * Auto-save a single answer (called when student selects an answer)
   */
  async saveAnswer(
    examId: string,
    answerData: {
      sessionId: string;
      questionId: string;
      selectedOption?: string;
      selectedOptions?: string[];
      trueFalseAnswers?: { [itemId: string]: boolean };
      matchingAnswers?: { [promptId: string]: string };
      fillAnswers?: { [blankId: string]: string };
      essayText?: string;
    }
  ): Promise<void> {
    return this.request(`/exams/${examId}/answers`, {
      method: 'POST',
      body: JSON.stringify(answerData),
    });
  }

  /**
   * Submit exam answers for grading
   */
  async submitExam(
    examId: string,
    submitData: {
      sessionId: string;
      userId?: string;
      answers: any[];
      timeSpent: number;
    }
  ): Promise<any> {
    return this.request(`/exams/${examId}/submit`, {
      method: 'POST',
      body: JSON.stringify(submitData),
    });
  }

  // ========== AI GRADING ==========

  async getAiGradingResult(gradingId: string): Promise<{
    id: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
    totalScore: number | null;
    maxScore: number | null;
    questionBreakdowns: Array<{
      questionId: string;
      questionOrder: number;
      pointsAwarded: number;
      maxPoints: number;
      aiFeedback: string;
      rubricItems: Array<{ label: string; pointsAwarded: number; maxPoints: number }>;
    }> | null;
    errorMessage: string | null;
  }> {
    return this.request(`/ai-grading/${gradingId}`);
  }

  // ========== ADMIN EXAM SESSION MANAGEMENT ==========

  /**
   * Admin: Get all exam sessions with filtering and pagination
   */
  async getAdminExamSessions(params?: {
    status?: string;
    search?: string;
    problemOnly?: string;
    examId?: string;
    subject?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query.append(key, String(value));
        }
      });
    }
    const queryString = query.toString();
    return this.request(`/exams/admin/exam-sessions${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Admin: Re-grade a session that is missing a Result record
   */
  async regradeSession(examId: string, sessionId: string): Promise<any> {
    return this.request(`/exams/${examId}/sessions/${sessionId}/regrade`, {
      method: 'POST',
    });
  }

  /**
   * Admin: Get full session detail with answers and questions
   */
  async getAdminSessionDetail(sessionId: string): Promise<any> {
    return this.request(`/exams/admin/exam-sessions/${sessionId}`);
  }

  // ========== TEST EXAM ENDPOINTS (for teachers/admins) ==========

  /**
   * Start a test exam session (preview mode, results not saved)
   */
  async startTestSession(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/test/start`, {
      method: 'POST',
    });
  }

  /**
   * Get all test sessions for an exam
   */
  async getTestSessions(examId: string): Promise<any[]> {
    return this.request<any[]>(`/exams/${examId}/test/sessions`);
  }

  /**
   * Get a specific test session with questions
   */
  async getTestSession(examId: string, testSessionId: string): Promise<any> {
    return this.request(`/exams/${examId}/test/${testSessionId}`);
  }

  /**
   * Send result email manually (student request)
   */
  async sendResultEmail(examId: string, resultId: string): Promise<any> {
    return this.request(`/exams/${examId}/results/${resultId}/email`, {
      method: 'POST',
    });
  }

  /**
   * Submit a review for an exam
   */
  async submitExamReview(
    examId: string,
    reviewData: {
      rating: number; // 1-5
      comment?: string;
      resultId?: string;
    }
  ): Promise<any> {
    return this.request(`/exams/${examId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  /**
   * Get reviews for an exam
   */
  async getExamReviews(examId: string, limit = 20, offset = 0): Promise<any> {
    return this.request(`/exams/${examId}/reviews?limit=${limit}&offset=${offset}`);
  }

  /**
   * Get review statistics for an exam
   */
  async getExamReviewStats(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/reviews/stats`);
  }

  /**
   * Get current user's review for an exam
   */
  async getMyExamReview(examId: string): Promise<any> {
    return this.request(`/exams/${examId}/reviews/my`);
  }

  /**
   * Transform backend exam data to frontend ExamInfo format
   */
  transformExamData(backendExam: any): any {
    // Use the actual type from backend and convert to lowercase
    const examType = (backendExam.type?.toLowerCase() || 'free') as
      | 'free'
      | 'static'
      | 'dynamic'
      | 'bgd';

    return {
      id: backendExam.id,
      title: backendExam.title,
      subject: this.mapSubjectToVietnamese(backendExam.subject),
      type: examType,
      duration: backendExam.duration,
      totalQuestions: backendExam.totalQuestions,
      totalPoints: backendExam.passingScore, // Using passingScore as totalPoints for now
      description: backendExam.description || '',
      price: backendExam.price || 0,
      rating: backendExam.rating || 0, // Use real rating from reviews
      enrolled: backendExam.enrolled || 0,
      level: this.mapDifficultyToLevel(backendExam.difficulty),
      difficulty: this.mapDifficultyToVietnamese(backendExam.difficulty),
      topics: [], // Will be populated from question categories later
      isFeatured: false, // Can be added to backend later
      isNew: this.isNewExam(backendExam.createdAt),
      status: backendExam.status, // Use status enum from backend
      hasActivePayment: backendExam.hasActivePayment ?? false,
      lastPaymentAt: backendExam.lastPaymentAt || null,
      lastCompletedSessionAt: backendExam.lastCompletedSessionAt || null,
      hasAiGrading: backendExam.hasAiGrading ?? false,
    };
  }

  /**
   * Map backend subject enum to Vietnamese
   */
  private mapSubjectToVietnamese(subject: string): string {
    const subjectMap: Record<string, string> = {
      MATHEMATICS: 'Toán học',
      ENGLISH: 'Tiếng Anh',
      PHYSICS: 'Vật lí',
      CHEMISTRY: 'Hóa học',
      BIOLOGY: 'Sinh học',
      HISTORY: 'Lịch sử',
      LITERATURE: 'Ngữ văn',
      GEOGRAPHY: 'Địa lý',
    };
    return subjectMap[subject] || subject;
  }

  /**
   * Map backend difficulty to Vietnamese
   */
  private mapDifficultyToVietnamese(difficulty: string): string {
    const difficultyMap: Record<string, string> = {
      easy: 'Dễ',
      medium: 'Trung bình',
      hard: 'Khó',
    };
    return difficultyMap[difficulty] || difficulty;
  }

  /**
   * Map backend difficulty to frontend level
   */
  private mapDifficultyToLevel(difficulty: string): 'basic' | 'intermediate' | 'advanced' {
    const levelMap: Record<string, 'basic' | 'intermediate' | 'advanced'> = {
      easy: 'basic',
      medium: 'intermediate',
      hard: 'advanced',
    };
    return levelMap[difficulty] || 'intermediate';
  }

  /**
   * Check if exam is new (created within last 30 days)
   */
  private isNewExam(createdAt: string): boolean {
    const createdDate = new Date(createdAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate > thirtyDaysAgo;
  }

  // ===== QUESTION BANK API METHODS =====

  /**
   * Get list of Question Banks for teacher
   */
  async getTeacherQuestionBanks(params?: {
    subject?: string;
    questionType?: string;
    status?: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<QuestionBankListResponse> {
    const queryParams: any = {
      isQuestionBank: 'true',
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    return this.request<QuestionBankListResponse>(
      `/question-collections${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * US1: Get published Question Banks for exam creation dropdown
   */
  async getQuestionBanksForExam(
    subject: string,
    organizationId?: string | null
  ): Promise<QuestionBank[]> {
    const params = new URLSearchParams({ subject });
    if (organizationId) params.set('organizationId', organizationId);
    return this.request<QuestionBank[]>(
      `/question-collections/question-banks/for-exam?${params.toString()}`
    );
  }

  /**
   * Get Question Bank details
   */
  async getQuestionBankDetails(id: string): Promise<QuestionBankDetail> {
    return this.request<QuestionBankDetail>(`/question-collections/${id}`);
  }

  /**
   * US3: Get questions from a Question Bank with psychometric metrics
   */
  async getQuestionBankQuestions(
    id: string,
    params?: {
      tag?: string;
      tags?: string[];
      difficulty?: string;
      search?: string;
      questionType?: string;
      limit?: number;
      offset?: number;
      hasCluster?: boolean;
      clusterId?: string;
    }
  ): Promise<QuestionBankQuestionListResponse> {
    // Build query string manually to handle arrays
    const queryParams = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // For arrays (tags), add each value separately
            value.forEach(item => queryParams.append(key, String(item)));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    const queryString = queryParams.toString();
    return this.request<QuestionBankQuestionListResponse>(
      `/question-collections/${id}/questions-with-metrics${queryString ? `?${queryString}` : ''}`
    );
  }

  async getCollectionQuestions(
    id: string,
    params?: {
      search?: string;
      questionType?: string;
      difficulty?: string[];
      tags?: string[];
      tagMatchType?: 'OR' | 'AND';
      minDValue?: number;
      pValueRange?: [number, number];
      rotationTier?: number;
      unusedOnly?: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    if (!params) {
      return this.request(`/question-collections/${id}/questions`);
    }

    // Build query string manually to handle arrays
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // For arrays, add each value separately
          value.forEach((v: any) => queryParams.append(key, String(v)));
        } else if (typeof value === 'boolean') {
          queryParams.append(key, String(value));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });

    const queryString = queryParams.toString();
    return this.request(
      `/question-collections/${id}/questions${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Preview dynamic question selection
   */
  async previewDynamicSelection(data: PreviewSelectionRequest): Promise<PreviewSelectionResponse> {
    return this.request<PreviewSelectionResponse>('/teacher/question-banks/preview-selection', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get Question Bank health metrics
   */
  async getQuestionBankHealth(id: string): Promise<QuestionBankHealth> {
    return this.request<QuestionBankHealth>(`/teacher/question-banks/${id}/health`);
  }

  // ========== STUDENT ANALYTICS ENDPOINTS ==========

  /**
   * Get student reports and insights
   * @param examStandard - Filter by VSAT/THPT/ALL (optional)
   */
  async getStudentReports(examStandard?: string): Promise<any> {
    const query = new URLSearchParams();
    if (examStandard) query.append('examStandard', examStandard);
    const queryString = query.toString();
    return this.request(`/analytics/student/reports${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get score distribution for a subject and exam standard
   * @param subject - Subject name (e.g., MATHEMATICS)
   * @param examStandard - Filter by VSAT/THPT/ALL (optional)
   */
  async getScoreDistribution(subject: string, examStandard?: string): Promise<any> {
    const query = new URLSearchParams({ subject });
    if (examStandard) query.append('examStandard', examStandard);
    return this.request(`/analytics/student/distribution?${query.toString()}`);
  }

  // ===== TAGS API METHODS =====

  /**
   * Get user's default tags
   */
  async getDefaultTags(): Promise<{ tags: string[] }> {
    return this.request<{ tags: string[] }>('/teacher/tags');
  }

  /**
   * Add a new default tag for user
   */
  async addDefaultTag(tag: string): Promise<{ message: string; tag: string }> {
    return this.request<{ message: string; tag: string }>('/teacher/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag }),
    });
  }

  /**
   * Remove a default tag for user
   */
  async removeDefaultTag(tag: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/teacher/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get official/restricted tags for current user
   */
  async getOfficialTags(): Promise<{ tags: string[] }> {
    return this.request<{ tags: string[] }>('/tags/official');
  }

  // ========== VSAT TEMPLATE ENDPOINTS ==========

  /**
   * Get all VSAT templates
   */
  async getAllVsatTemplates(): Promise<any[]> {
    return this.request<any[]>('/teacher/vsat-templates');
  }

  /**
   * Get VSAT template by subject
   */
  async getVsatTemplateBySubject(subject: string): Promise<any> {
    return this.request<any>(`/teacher/vsat-templates/${subject}`);
  }

  /**
   * Get VSAT template by ID
   */
  async getVsatTemplateById(id: string): Promise<any> {
    return this.request<any>(`/teacher/vsat-templates/id/${id}`);
  }

  /**
   * Create VSAT template (ADMIN only)
   */
  async createVsatTemplate(data: CreateVsatTemplateData): Promise<any> {
    return this.request<any>('/teacher/vsat-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update VSAT template (ADMIN only)
   */
  async updateVsatTemplate(id: string, data: UpdateVsatTemplateData): Promise<any> {
    return this.request<any>(`/teacher/vsat-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete VSAT template (ADMIN only, soft delete)
   */
  async deleteVsatTemplate(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/teacher/vsat-templates/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Duplicate VSAT template (ADMIN only)
   */
  async duplicateVsatTemplate(id: string): Promise<any> {
    return this.request<any>(`/teacher/vsat-templates/${id}/duplicate`, {
      method: 'POST',
    });
  }

  // ========== THPT TEMPLATE ENDPOINTS ==========

  /**
   * Get all THPT templates
   */
  async getAllThptTemplates(): Promise<any[]> {
    return this.request<any[]>('/teacher/thpt-templates');
  }

  /**
   * Get THPT template by subject
   */
  async getThptTemplateBySubject(subject: string): Promise<any> {
    return this.request<any>(`/teacher/thpt-templates/${subject}`);
  }

  /**
   * Get THPT template by ID
   */
  async getThptTemplateById(id: string): Promise<any> {
    return this.request<any>(`/teacher/thpt-templates/id/${id}`);
  }

  /**
   * Create THPT template (ADMIN only)
   */
  async createThptTemplate(data: CreateThptTemplateData): Promise<any> {
    return this.request<any>('/teacher/thpt-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update THPT template (ADMIN only)
   */
  async updateThptTemplate(id: string, data: UpdateThptTemplateData): Promise<any> {
    return this.request<any>(`/teacher/thpt-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete THPT template (ADMIN only, soft delete)
   */
  async deleteThptTemplate(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/teacher/thpt-templates/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Duplicate THPT template (ADMIN only)
   */
  async duplicateThptTemplate(id: string): Promise<any> {
    return this.request<any>(`/teacher/thpt-templates/${id}/duplicate`, {
      method: 'POST',
    });
  }

  // ========== PAYMENT MANAGEMENT ENDPOINTS ==========

  /**
   * Get payment details (Admin)
   */
  async getPaymentDetail(id: string): Promise<any> {
    return this.request<any>(`/payment/detail/${id}`);
  }

  /**
   * Confirm payment (Admin)
   */
  async confirmPayment(id: string): Promise<any> {
    return this.request<any>(`/payment/confirm/${id}`, {
      method: 'POST',
    });
  }

  /**
   * Reject payment (Admin)
   */
  async rejectPayment(id: string): Promise<any> {
    return this.request<any>(`/payment/reject/${id}`, {
      method: 'POST',
    });
  }

  /**
   * Refund payment (Admin)
   */
  async refundPayment(id: string): Promise<any> {
    return this.request<any>(`/payment/refund/${id}`, {
      method: 'POST',
    });
  }

  /**
   * Check payment status with BIDV (Admin)
   */
  async checkPaymentStatusWithBIDV(id: string): Promise<{
    status: string;
    previousStatus?: string;
    changed: boolean;
    message: string;
    bidvTransactionId?: string | null;
    paidAt?: string | null;
  }> {
    return this.request(`/payment/check-status/${id}`, {
      method: 'POST',
    });
  }

  /**
   * Bulk check all pending payments with BIDV (Admin)
   */
  async bulkCheckPendingPayments(): Promise<{
    totalChecked: number;
    resolved: number;
    failed: number;
    stillPending: number;
    errors: number;
    details: Array<{ id: string; orderId: string; previousStatus: string; newStatus: string }>;
  }> {
    return this.request(`/payment/bulk-check-pending`, {
      method: 'POST',
    });
  }

  /**
   * Trigger BIDV daily reconciliation for a specific date (Admin)
   */
  async triggerReconciliation(transDate: string): Promise<{
    transDate: string;
    totalBIDVTransactions: number;
    resolved: number;
    alreadyCompleted: number;
    notFound: number;
    errors: number;
    message: string;
    details: Array<{ orderId: string; action: string; bidvTxId?: string }>;
  }> {
    return this.request(`/payment/reconcile`, {
      method: 'POST',
      body: JSON.stringify({ transDate }),
    });
  }

  /**
   * Trigger BIDV reconciliation for a date range (Admin, max 31 days).
   * Both fromDate and toDate must be in YYYYMMDD format.
   */
  async triggerReconciliationRange(
    fromDate: string,
    toDate: string
  ): Promise<{
    fromDate: string;
    toDate: string;
    daysProcessed: number;
    totalBIDVTransactions: number;
    resolved: number;
    alreadyCompleted: number;
    notFound: number;
    errors: number;
    message: string;
    perDay: Array<{
      transDate: string;
      totalBIDVTransactions: number;
      resolved: number;
      alreadyCompleted: number;
      notFound: number;
      errors: number;
      error?: string;
    }>;
  }> {
    return this.request(`/payment/reconcile/range`, {
      method: 'POST',
      body: JSON.stringify({ fromDate, toDate }),
    });
  }

  async uploadBidvReconciliationFile(file: File): Promise<BidvReconciliationPreview> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<BidvReconciliationPreview>(`/payment/bidv-file`, {
      method: 'POST',
      body: formData,
    });
  }

  async applyBidvReconciliation(
    uploadId: string,
    decisions: Array<{ bidvRef: string; action: BidvReconcileAction }>
  ): Promise<BidvApplyResult> {
    return this.request<BidvApplyResult>(`/payment/bidv-file/${uploadId}/apply`, {
      method: 'POST',
      body: JSON.stringify({ decisions }),
    });
  }

  /**
   * Update B2B invoice information on a payment transaction (Admin)
   * Used by portal admins to add company name, tax code, and address for corporate invoices
   */
  async updateB2BInvoiceInfo(
    id: string,
    data: {
      buyerCompanyName: string;
      buyerTaxCode: string;
      buyerCompanyAddress: string;
      buyerFullName?: string;
    }
  ): Promise<{
    success: boolean;
    message: string;
    buyerCompanyName: string | null;
    buyerTaxCode: string | null;
    buyerCompanyAddress: string | null;
  }> {
    return this.request(`/payment/${id}/b2b-info`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ========== TEMPLATE USAGE ENDPOINTS ==========

  /**
   * Create exam from template
   */
  async createExamFromTemplate(
    templateId: string,
    templateType: 'VSAT' | 'THPT',
    data: CreateExamFromTemplateData
  ): Promise<any> {
    return this.request<any>(`/teacher/templates/${templateId}/create-exam`, {
      method: 'POST',
      body: JSON.stringify({
        templateType,
        ...data,
      }),
    });
  }

  /**
   * Get template usage statistics
   */
  async getTemplateUsageStats(
    templateId: string,
    templateType: 'VSAT' | 'THPT'
  ): Promise<TemplateUsageStats> {
    return this.request<TemplateUsageStats>(
      `/teacher/templates/${templateId}/usage-stats?templateType=${templateType}`
    );
  }

  /**
   * Get template version history
   */
  async getTemplateVersions(
    templateId: string,
    templateType: 'VSAT' | 'THPT'
  ): Promise<TemplateVersion[]> {
    return this.request<TemplateVersion[]>(
      `/teacher/templates/${templateId}/versions?templateType=${templateType}`
    );
  }

  /**
   * Get specific template version
   */
  async getTemplateVersion(
    templateId: string,
    templateType: 'VSAT' | 'THPT',
    version: number
  ): Promise<TemplateVersion> {
    return this.request<TemplateVersion>(
      `/teacher/templates/${templateId}/versions/${version}?templateType=${templateType}`
    );
  }

  /**
   * Rollback template to a specific version
   */
  async rollbackTemplate(
    templateId: string,
    templateType: 'VSAT' | 'THPT',
    version: number
  ): Promise<any> {
    return this.request<any>(
      `/teacher/templates/${templateId}/rollback/${version}?templateType=${templateType}`,
      {
        method: 'POST',
      }
    );
  }

  // ========== Career Orientation Methods ==========

  // Groups
  async getCareerOrientationGroups(filters?: { search?: string; isActive?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

    return this.request<CareerOrientationGroup[]>(
      `/career-orientation/groups${params.toString() ? `?${params.toString()}` : ''}`,
      {
        method: 'GET',
      }
    );
  }

  async getCareerOrientationGroupById(groupId: string) {
    return this.request<CareerOrientationGroup>(`/career-orientation/groups/${groupId}`, {
      method: 'GET',
    });
  }

  async createCareerOrientationGroup(data: CreateCareerOrientationGroupDto) {
    return this.request<CareerOrientationGroup>('/career-orientation/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCareerOrientationGroup(groupId: string, data: UpdateCareerOrientationGroupDto) {
    return this.request<CareerOrientationGroup>(`/career-orientation/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCareerOrientationGroup(groupId: string) {
    return this.request<void>(`/career-orientation/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  // Questions
  async getCareerOrientationQuestions(filters?: {
    search?: string;
    groupId?: string;
    isActive?: boolean;
  }) {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.groupId) params.append('groupId', filters.groupId);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

    return this.request<CareerOrientationQuestion[]>(
      `/career-orientation/questions${params.toString() ? `?${params.toString()}` : ''}`,
      {
        method: 'GET',
      }
    );
  }

  async getCareerOrientationQuestionById(questionId: string) {
    return this.request<CareerOrientationQuestion>(`/career-orientation/questions/${questionId}`, {
      method: 'GET',
    });
  }

  async createCareerOrientationQuestion(data: CreateCareerOrientationQuestionDto) {
    return this.request<CareerOrientationQuestion>('/career-orientation/questions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCareerOrientationQuestion(
    questionId: string,
    data: UpdateCareerOrientationQuestionDto
  ) {
    return this.request<CareerOrientationQuestion>(`/career-orientation/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCareerOrientationQuestion(questionId: string) {
    return this.request<void>(`/career-orientation/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  // ========== Career Orientation Public/Student Endpoints ==========

  /**
   * Get all active groups with questions (public endpoint)
   */
  async getCareerOrientationPublicGroups(): Promise<CareerOrientationGroupWithQuestions[]> {
    return this.request<CareerOrientationGroupWithQuestions[]>('/career-orientation/public/groups');
  }

  /**
   * Get public assessment statistics (total assessments) - no auth required
   */
  async getCareerOrientationPublicStats(): Promise<{ totalAssessments: number }> {
    return this.request<{ totalAssessments: number }>('/career-orientation/public/stats');
  }

  /**
   * Get all active university majors grouped by career group (public endpoint)
   * Single source of truth for /nganh-hoc page — same data as Phase 2 CAT Profile
   */
  async getPublicMajors(): Promise<
    Array<{
      groupCode: string;
      groupName: string;
      majors: Array<{
        id: string;
        name: string;
        subjects: Array<{ subject: string; subjectName: string; order: number }>;
        primarySubject: string | null;
        threshold: number | null;
      }>;
    }>
  > {
    return this.request('/career-orientation/public/majors');
  }

  /**
   * Recommend majors based on combo scores from V-SAT conversion tool (public, no auth)
   * Uses Delta clustering to categorize recommendations
   */
  async recommendByScores(
    comboScores: Array<{
      comboName: string;
      score: number;
      subjects: string[];
    }>
  ): Promise<{
    totalMajors: number;
    totalPassed: number;
    recommendations: Array<{
      groupCode: string;
      groupName: string;
      totalMajors: number;
      passedMajors: number;
      majors: Array<{
        majorName: string;
        groupCode: string;
        groupName: string;
        examBlocks: string[];
        matchedCombo: string;
        threshold: number;
        userAvgScore: number;
        delta: number;
        deltaCategory: 'MO_UOC' | 'VUA_SUC' | 'PHU_HOP' | 'AN_TOAN';
        passed: boolean;
        subjects: Array<{ subject: string; subjectName: string }>;
      }>;
    }>;
  }> {
    return this.request('/career-orientation/public/recommend-by-scores', {
      method: 'POST',
      body: JSON.stringify({ comboScores }),
    });
  }

  /**
   * Get user's career orientation assessments history
   */
  async getUserCareerOrientationAssessments(
    limit: number = 10,
    offset: number = 0
  ): Promise<{ data: any[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return this.request<{ data: any[]; total: number; limit: number; offset: number }>(
      `/career-orientation/assessments?${params.toString()}`
    );
  }

  /**
   * Get user's exam results history
   */
  async getUserExamResults(
    limit: number = 50,
    offset: number = 0
  ): Promise<{ data: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return this.request<{ data: any[]; total: number }>(
      `/users/me/exam-results?${params.toString()}`
    );
  }

  /**
   * Get a specific exam result detail (with gradeSummary)
   */
  async getExamResultDetail(resultId: string): Promise<any> {
    return this.request<any>(`/users/me/exam-results/${resultId}`);
  }

  /**
   * Create a new career orientation assessment
   * educationLevel is optional - only needed for full report
   */
  async createCareerOrientationAssessment(
    educationLevel?: 'THCS' | 'THPT'
  ): Promise<CareerOrientationAssessment> {
    return this.request<CareerOrientationAssessment>('/career-orientation/assessments', {
      method: 'POST',
      body: JSON.stringify(educationLevel ? { educationLevel } : {}),
    });
  }

  /**
   * Update education level for an assessment (required before viewing full report)
   */
  async updateCareerOrientationEducationLevel(
    assessmentId: string,
    educationLevel: 'THCS' | 'THPT'
  ): Promise<CareerOrientationAssessment> {
    return this.request<CareerOrientationAssessment>(
      `/career-orientation/assessments/${assessmentId}/education-level`,
      {
        method: 'PUT',
        body: JSON.stringify({ educationLevel }),
      }
    );
  }

  /**
   * Submit answers for an assessment
   */
  async submitCareerOrientationAnswers(
    assessmentId: string,
    answers: Array<{ questionId: string; answer: boolean }>
  ): Promise<{ success: boolean; answeredCount: number }> {
    return this.request<{ success: boolean; answeredCount: number }>(
      `/career-orientation/assessments/${assessmentId}/answers`,
      {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }
    );
  }

  /**
   * Complete assessment and calculate scores
   */
  async completeCareerOrientationAssessment(
    assessmentId: string
  ): Promise<CareerOrientationAssessmentCompleteResponse> {
    return this.request<CareerOrientationAssessmentCompleteResponse>(
      `/career-orientation/assessments/${assessmentId}/complete`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Get assessment result (free report)
   */
  async getCareerOrientationAssessmentResult(
    assessmentId: string
  ): Promise<CareerOrientationAssessmentResult> {
    return this.request<CareerOrientationAssessmentResult>(
      `/career-orientation/assessments/${assessmentId}`
    );
  }

  /**
   * Get full report (paid report)
   */
  async getCareerOrientationFullReport(
    assessmentId: string
  ): Promise<CareerOrientationAssessmentResult> {
    return this.request<CareerOrientationAssessmentResult>(
      `/career-orientation/assessments/${assessmentId}/full-report`
    );
  }

  /**
   * Get session-scoped questions and existing answers for an assessment
   */
  async getCareerOrientationAssessmentQuestions(
    assessmentId: string
  ): Promise<CareerOrientationAssessmentQuestionsResponse> {
    return this.request<CareerOrientationAssessmentQuestionsResponse>(
      `/career-orientation/assessments/${assessmentId}/questions`
    );
  }

  /**
   * Get full report PDF link (paid, session-scoped)
   */
  async getCareerOrientationFullReportPdf(
    assessmentId: string
  ): Promise<CareerOrientationFullReportPdfResponse> {
    return this.request<CareerOrientationFullReportPdfResponse>(
      `/career-orientation/assessments/${assessmentId}/full-report/pdf`
    );
  }

  /**
   * Link payment transaction to assessment and grant full report access
   */
  async linkPaymentToCareerOrientationAssessment(
    assessmentId: string,
    paymentTransactionId: string
  ): Promise<void> {
    return this.request<void>(`/career-orientation/assessments/${assessmentId}/link-payment`, {
      method: 'PUT',
      body: JSON.stringify({ paymentTransactionId }),
    });
  }

  /**
   * Grant full report access without payment (for mock/testing mode)
   */
  async grantCareerOrientationFullReportAccess(assessmentId: string): Promise<void> {
    return this.request<void>(`/career-orientation/assessments/${assessmentId}/grant-access`, {
      method: 'PUT',
    });
  }

  // ========== CAREER ORIENTATION ADMIN ENDPOINTS ==========

  /**
   * Get overall assessment statistics (Admin only)
   */
  async getCareerAssessmentStatistics(params?: { excludeTestPayments?: boolean }): Promise<{
    totalAssessments: number;
    completedAssessments: number;
    inProgressAssessments: number;
    abandonedAssessments: number;
    completionRate: number;
    paidAssessments: number;
    conversionRate: number;
    totalRevenue: number;
    thptCount: number;
    thcsCount: number;
    todayCount: number;
    weekCount: number;
    monthCount: number;
  }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && (typeof value === 'boolean' || value !== '')) {
          query.append(key, String(value));
        }
      });
    }
    const queryString = query.toString();
    return this.request(
      `/career-orientation/admin/assessments/statistics${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Get paginated list of assessments with filters (Admin only)
   */
  async getCareerAssessments(params?: {
    search?: string;
    status?: string;
    hasPaid?: boolean;
    educationLevel?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    excludeTestPayments?: boolean;
  }): Promise<{
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && (typeof value === 'boolean' || value !== '')) {
          query.append(key, String(value));
        }
      });
    }
    const queryString = query.toString();
    return this.request(
      `/career-orientation/admin/assessments${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Get detailed assessment information (Admin only)
   */
  async getCareerAssessmentDetail(assessmentId: string): Promise<any> {
    return this.request(`/career-orientation/admin/assessments/${assessmentId}`);
  }

  /**
   * Get payment conversion statistics (Admin only)
   */
  async getCareerConversionStats(
    startDate?: string,
    endDate?: string
  ): Promise<{
    overallConversionRate: number;
    totalCompleted: number;
    totalPaid: number;
    totalRevenue: number;
    averageRevenuePerConversion: number;
    dailyStats: Array<{
      date: string;
      completed: number;
      paid: number;
      conversionRate: number;
      revenue: number;
    }>;
    byEducationLevel: {
      thpt: {
        completed: number;
        paid: number;
        conversionRate: number;
      };
      thcs: {
        completed: number;
        paid: number;
        conversionRate: number;
      };
    };
  }> {
    const query = new URLSearchParams();
    if (startDate) query.append('startDate', startDate);
    if (endDate) query.append('endDate', endDate);
    const queryString = query.toString();
    return this.request(
      `/career-orientation/admin/conversion-stats${queryString ? `?${queryString}` : ''}`
    );
  }

  // ========== QUESTION FILTER OPTIONS ==========

  /**
   * Get all available tags for filtering questions
   * @param organizationId - Filter by organization ID. Use 'null' for personal questions.
   * @param collectionId - Filter by collection/question bank ID. If provided, only returns tags from questions in that collection.
   */
  async getAvailableTags(organizationId?: string | null, collectionId?: string): Promise<string[]> {
    try {
      const params = new URLSearchParams();
      if (organizationId !== undefined) {
        params.append('organizationId', organizationId === null ? 'null' : organizationId);
      }
      if (collectionId) {
        params.append('collectionId', collectionId);
      }
      const queryString = params.toString();
      const response = await this.request<any>(
        `/teacher/questions/tags${queryString ? `?${queryString}` : ''}`
      );
      // Handle both direct array and wrapped response
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching available tags:', error);
      return [];
    }
  }

  /**
   * Get all available clusters for filtering questions
   */
  async getAvailableClusters(
    organizationId?: string | null,
    questionBankId?: string
  ): Promise<any[]> {
    try {
      let params = organizationId
        ? `?organizationId=${organizationId}`
        : organizationId === null
          ? '?organizationId=null'
          : '';
      if (questionBankId) params += `${params ? '&' : '?'}questionBankId=${questionBankId}`;
      const response = await this.request<any>(`/teacher/questions/clusters${params}`);
      // Handle both direct array and wrapped response
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching available clusters:', error);
      return [];
    }
  }

  /**
   * Get all available collections/question banks for filtering questions
   */
  async getAvailableCollections(organizationId?: string | null): Promise<any[]> {
    try {
      const params = organizationId
        ? `?organizationId=${organizationId}`
        : organizationId === null
          ? '?organizationId=null'
          : '';
      const response = await this.request<any>(`/teacher/questions/collections${params}`);
      // Handle both direct array and wrapped response
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching available collections:', error);
      return [];
    }
  }

  /**
   * Export all career orientation groups and questions to JSON
   */
  async exportCareerOrientationData(): Promise<Blob> {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/career-orientation/export`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-VSAT-Bypass': 'website-login',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Export failed');
    }

    return response.blob();
  }

  /**
   * Import career orientation groups and questions from JSON file
   */
  async importCareerOrientationData(file: File): Promise<{
    success: boolean;
    report: {
      groupsCreated: number;
      groupsUpdated: number;
      questionsCreated: number;
      questionsUpdated: number;
      errors: string[];
      warnings: string[];
    };
  }> {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/career-orientation/import`, {
      method: 'POST',
      headers: {
        'X-VSAT-Bypass': 'website-login',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Import failed');
    }

    return response.json();
  }

  /**
   * Send assessment report via email
   */
  async sendCareerOrientationReportEmail(
    assessmentId: string,
    email?: string
  ): Promise<{ success: boolean; message: string }> {
    const token = localStorage.getItem('accessToken');

    const response = await fetch(
      `${API_BASE_URL}/career-orientation/assessments/${assessmentId}/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-VSAT-Bypass': 'website-login',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ email }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send email');
    }

    return response.json();
  }

  /**
   * Generate PDF report using backend Puppeteer
   */
  async generateCareerOrientationPDF(data: {
    userName: string;
    userClass: string;
    testDate: string;
    assessmentId: string;
    educationLevel: 'THCS' | 'THPT';
    top3Codes: Array<{ code: string; name: string; shortName: string; color: string }>;
    top3Scores: Array<{ score: number }>;
    allScores: Array<{ id: string; name: string; actualScore: number; maxScore: number }>;
  }): Promise<ArrayBuffer> {
    const token = localStorage.getItem('accessToken');

    const response = await fetch(`${API_BASE_URL}/career-orientation/generate-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-VSAT-Bypass': 'website-login',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'PDF generation failed');
    }

    return response.arrayBuffer();
  }

  // ========== NOTIFICATIONS ==========

  /**
   * Get user notifications with filtering and pagination
   */
  async getNotifications(query?: {
    type?: string;
    isRead?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{
    data: any[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams();
    if (query?.type) params.append('type', query.type);
    if (query?.isRead !== undefined) params.append('isRead', String(query.isRead));
    if (query?.limit) params.append('limit', String(query.limit));
    if (query?.offset) params.append('offset', String(query.offset));

    const queryString = params.toString();
    return this.request<{
      data: any[];
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>(`/notifications${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get count of unread notifications
   */
  async getUnreadCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  /**
   * Mark one or multiple notifications as read
   */
  async markNotificationAsRead(ids: string[]): Promise<void> {
    if (ids.length === 1) {
      return this.request<void>(`/notifications/${ids[0]}/read`, {
        method: 'PATCH',
      });
    } else {
      return this.request<void>('/notifications/mark-read', {
        method: 'PATCH',
        body: JSON.stringify({ ids }),
      });
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead(): Promise<void> {
    return this.request<void>('/notifications/mark-all-read', {
      method: 'PATCH',
    });
  }

  /**
   * Delete a notification (soft delete)
   */
  async deleteNotification(id: string): Promise<void> {
    return this.request<void>(`/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get user notification preferences
   */
  async getNotificationPreferences(): Promise<{
    id: string;
    userId: string;
    enableExamReminders: boolean;
    enableResultNotifs: boolean;
    enablePaymentNotifs: boolean;
    enableSystemUpdates: boolean;
    enableUserManagement: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    return this.request('/notifications/preferences');
  }

  /**
   * Update user notification preferences
   */
  async updateNotificationPreferences(preferences: {
    enableExamReminders?: boolean;
    enableResultNotifs?: boolean;
    enablePaymentNotifs?: boolean;
    enableSystemUpdates?: boolean;
    enableUserManagement?: boolean;
  }): Promise<void> {
    return this.request<void>('/notifications/preferences', {
      method: 'PATCH',
      body: JSON.stringify(preferences),
    });
  }

  // ===== ACCESS CODE MANAGEMENT =====

  async createAccessCode(data: any) {
    return this.request('/access-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAccessCodes(filters?: any) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    return this.request(`/access-codes${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  }

  async getAccessCode(id: string) {
    return this.request(`/access-codes/${id}`, {
      method: 'GET',
    });
  }

  async updateAccessCode(id: string, data: any) {
    return this.request(`/access-codes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAccessCode(id: string) {
    return this.request(`/access-codes/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleAccessCodeStatus(id: string) {
    return this.request(`/access-codes/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async getAccessCodeUsageLogs(id: string) {
    return this.request(`/access-codes/${id}/usage`, {
      method: 'GET',
    });
  }

  async getAccessCodeStats(id: string) {
    return this.request(`/access-codes/${id}/stats`, {
      method: 'GET',
    });
  }

  async getApplicableItems() {
    return this.request('/access-codes/items/applicable', {
      method: 'GET',
    });
  }

  async validateAccessCode(
    code: string,
    examId?: string,
    assessmentId?: string,
    systemContext?: string
  ) {
    return this.request('/access-codes/public/validate', {
      method: 'POST',
      body: JSON.stringify({ code, examId, assessmentId, systemContext }),
    });
  }

  async getMisaInvoices(params: PaymentQueryParams) {
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    return this.request(`/payment/misa/invoices?${query.toString()}`, {
      method: 'GET',
    });
  }

  async syncMisaInvoice(id: string) {
    return this.request(`/payment/misa/invoices/${id}/sync`, {
      method: 'POST',
    });
  }

  async createMisaInvoice(id: string) {
    return this.request(`/payment/misa/invoices/${id}/create`, {
      method: 'POST',
    });
  }

  async downloadMisaPdf(id: string, series?: string): Promise<Blob> {
    const query = series ? `?series=${encodeURIComponent(series)}` : '';
    return this.downloadFile(`/payment/misa/invoices/${id}/download-pdf${query}`);
  }

  async downloadMisaXml(id: string, series?: string): Promise<Blob> {
    const query = series ? `?series=${encodeURIComponent(series)}` : '';
    return this.downloadFile(`/payment/misa/invoices/${id}/download-xml${query}`);
  }

  async getMisaInvoiceList(filters: any) {
    return this.request('/payment/misa/invoices/paging', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  async getMisaInvoiceViewLink(id: string) {
    return this.request<{ success: boolean; link: string }>(
      `/payment/misa/invoices/${id}/view-link`
    );
  }

  async sendMisaEmail(id: string, data: any = {}) {
    return this.request(`/payment/misa/invoices/${id}/send-email`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== LEAF WALLET (Ví Lá) =====

  async getWalletBalance(): Promise<WalletBalanceResponse> {
    return this.request<WalletBalanceResponse>('/leaf-wallet/me');
  }

  async getWalletTransactions(params?: {
    page?: number;
    limit?: number;
    type?: string;
    from?: string;
    to?: string;
  }): Promise<{ data: LeafTransactionItem[]; meta: PaginationMeta }> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.type) query.append('type', params.type);
    if (params?.from) query.append('from', params.from);
    if (params?.to) query.append('to', params.to);
    const qs = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/leaf-wallet/transactions${qs}`);
  }

  async getTopUpPackages(): Promise<TopUpPackageItem[]> {
    return this.request<TopUpPackageItem[]>('/leaf-wallet/packages');
  }

  async purchaseExamWithLeaves(examId: string): Promise<{
    examId: string;
    leafCost: number;
    newBalance: number;
    examTitle: string;
  }> {
    return this.request('/leaf-wallet/purchase-exam', {
      method: 'POST',
      body: JSON.stringify({ examId }),
    });
  }

  async canPurchaseExamWithLeaves(examId: string): Promise<{
    canPurchase: boolean;
    leafCost: number;
    currentBalance: number;
    shortfall: number;
  }> {
    return this.request(`/leaf-wallet/can-purchase/${examId}`);
  }

  // ===== LEAF WALLET ADMIN =====

  async getLeafWalletConfigs(): Promise<any[]> {
    return this.request('/admin/leaf-wallet/configs');
  }

  async updateLeafWalletConfig(key: string, value: string): Promise<any> {
    return this.request('/admin/leaf-wallet/configs', {
      method: 'PATCH',
      body: JSON.stringify({ key, value }),
    });
  }

  async getAdminTopUpPackages(): Promise<any[]> {
    return this.request('/admin/leaf-wallet/packages');
  }

  async createTopUpPackage(data: any): Promise<any> {
    return this.request('/admin/leaf-wallet/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTopUpPackage(id: string, data: any): Promise<any> {
    return this.request(`/admin/leaf-wallet/packages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTopUpPackage(id: string): Promise<void> {
    return this.request(`/admin/leaf-wallet/packages/${id}`, { method: 'DELETE' });
  }

  async getAdminBonusTiers(): Promise<any[]> {
    return this.request('/admin/leaf-wallet/bonus-tiers');
  }

  async createBonusTier(data: any): Promise<any> {
    return this.request('/admin/leaf-wallet/bonus-tiers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBonusTier(id: string, data: any): Promise<any> {
    return this.request(`/admin/leaf-wallet/bonus-tiers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBonusTier(id: string): Promise<void> {
    return this.request(`/admin/leaf-wallet/bonus-tiers/${id}`, { method: 'DELETE' });
  }

  async getAdminUserWallet(userId: string): Promise<any> {
    return this.request(`/admin/leaf-wallet/users/${userId}/wallet`);
  }

  async adjustUserLeafBalance(userId: string, amount: number, memo: string): Promise<any> {
    return this.request(`/admin/leaf-wallet/users/${userId}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ amount, memo }),
    });
  }

  async getLeafWalletStatistics(): Promise<any> {
    return this.request('/admin/leaf-wallet/statistics');
  }
}

// ========== Career Orientation Types ==========

export interface CareerOrientationGroup {
  id: string;
  code: string;
  name: string;
  shortDesc: string;
  shortDescPoints: string[];
  fullDesc?: string;
  fullDescDetails?: Array<{ title: string; content: string }>;
  thptMajors: string[];
  thptMajorGroups?: Array<{ name: string; majors: string[] }>;
  thcsCareers: string[];
  thcsCareerGroups?: Array<{ name: string; careers: string[] }>;
  order: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  _count?: {
    questions: number;
  };
  questions?: CareerOrientationQuestion[];
}

export interface CareerOrientationQuestion {
  id: string;
  code: string;
  groupId: string;
  statement: string;
  label: string;
  order: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  group?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface CreateCareerOrientationGroupDto {
  name: string;
  shortDesc: string;
  shortDescPoints?: string[];
  fullDesc?: string;
  fullDescDetails?: Array<{ title: string; content: string }>;
  thptMajors?: string[];
  thcsCareers?: string[];
  order: number;
  isActive?: boolean;
}

export interface UpdateCareerOrientationGroupDto {
  name?: string;
  shortDesc?: string;
  shortDescPoints?: string[];
  fullDesc?: string;
  fullDescDetails?: Array<{ title: string; content: string }>;
  thptMajors?: string[];
  thcsCareers?: string[];
  order?: number;
  isActive?: boolean;
}

export interface CreateCareerOrientationQuestionDto {
  groupId: string;
  statement: string;
  order: number;
  isActive?: boolean;
}

export interface UpdateCareerOrientationQuestionDto {
  statement?: string;
  label?: string;
  groupId?: string;
  order?: number;
  isActive?: boolean;
}

// Career Orientation Assessment Types
export interface CareerOrientationGroupWithQuestions extends CareerOrientationGroup {
  questions: CareerOrientationQuestion[];
}

export interface CareerOrientationAssessment {
  id: string;
  userId: string;
  educationLevel: 'THCS' | 'THPT';
  status: 'IN_PROGRESS' | 'COMPLETED';
  startedAt: Date;
  completedAt?: Date;
  totalQuestions: number;
  answeredCount: number;
  paymentTransactionId?: string;
  hasFullReportAccess: boolean;
}

export interface CareerOrientationGroupResult {
  id: string;
  assessmentId: string;
  groupId: string;
  rank: number;
  group: CareerOrientationGroup;
}

export interface CareerOrientationAssessmentScore {
  id: string;
  assessmentId: string;
  groupId: string;
  score: number;
  rank?: number;
  group: CareerOrientationGroup;
}

export interface CareerOrientationAssessmentResult extends CareerOrientationAssessment {
  groupResults: CareerOrientationGroupResult[];
  scores: CareerOrientationAssessmentScore[];
}

export interface CareerOrientationAssessmentQuestionsResponse {
  assessmentId: string;
  totalQuestions: number;
  groups: CareerOrientationGroupWithQuestions[];
  answers: Array<{ questionId: string; answer: boolean; answeredAt?: string }>;
}

export interface CareerOrientationAssessmentCompleteResponse {
  assessmentId: string;
  freeResultRef: string;
  result: CareerOrientationAssessmentResult;
}

export interface CareerOrientationFullReportPdfResponse {
  assessmentId: string;
  pdfUrl: string;
  paid: boolean;
}

export const apiService = new ApiService();

// ========== Leaf Wallet Types ==========

export interface WalletBalanceResponse {
  totalBalance: number;
  paidBalance: number;
  bonusBalance: number;
  rewardBalance: number;
  giftBalance: number;
  expiringWithin30Days: number;
}

export interface LeafTransactionItem {
  id: string;
  walletId: string;
  batchId?: string;
  type: string;
  amount: number;
  balanceAfter: number;
  examId?: string;
  memo?: string;
  createdAt: string;
  batch?: {
    type: string;
    expiresAt?: string;
  };
}

export interface TopUpPackageItem {
  id: string;
  name: string;
  amountVnd: number;
  leafAmount: number;
  bonusPercent: number;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}
